import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';

// Lead inbox — MTR page → "Lead inbox" tab (API: /api/leads, server/leads.js).
//
// Every renter inquiry from Zillow Rental Manager, Furnished Finder and Avail
// in one list. The Leads agent (an always-on Mac running Chrome) reads the
// platform inboxes into here; replies written here are queued for the agent,
// which types them back into the platform thread.
//
// Lifecycle: MtrPage keeps ONE instance and re-mounts it into a fresh host on
// every MtrPage.draw(), so every piece of state (selected lead, loaded thread,
// per-lead drafts, filters, scroll intent) lives on the instance and mount()
// re-renders from it. The 20 s poll self-terminates once its root detaches —
// there is no unmount hook (same rule as QuoInboxPage).
//
// Safety: message text comes from strangers on the internet. Every server
// string goes through esc(), message bodies render as text (white-space:
// pre-wrap, never HTML), and only http(s) URLs ever reach an href.

const API = '/api/leads';
const POLL_MS = 20000;

type PlatformId = 'zillow' | 'ff' | 'avail';
type View = 'open' | 'all' | 'archived';
type Tone = 'ok' | 'err' | 'info';

interface PlatformBrief { label?: string; short?: string; enabled?: boolean; state?: string; state_label?: string; last_sync_at?: string | null; message?: string; }
interface AgentBrief { online?: boolean; last_seen_at?: string | null; version?: string | null; host?: string | null; }
interface LeadSummary {
  id: string; platform: string; platform_label?: string;
  name?: string; email?: string | null; phone?: string | null;
  property_text?: string; listing_id?: string | null; listing_name?: string | null; match_via?: string | null;
  intent?: string; tags?: string[]; status?: string; assignee?: string | null; preview?: string;
  last_message_at?: string | null; last_in_at?: string | null; last_out_at?: string | null;
  first_in_at?: string | null; first_response_at?: string | null;
  waiting?: boolean; waiting_since?: string | null; due_at?: string | null; breached?: boolean;
  message_count?: number; pending_replies?: number; reply_problem?: string | null;
  reply_ai_wait?: boolean;   // a reply here is waiting out an AI outage (server/leads.js decorate)
  thread_url?: string | null; mtr_lead_id?: string | null; duplicate_of?: string | null; partial?: boolean;
  created_at?: string; updated_at?: string;
}
interface LeadMessage { id?: string; direction: string; author?: string; text?: string; at?: string | null; at_text?: string; via?: string; job_id?: string; by?: string; }
interface LeadDetail extends LeadSummary {
  profile?: Record<string, any> | null;
  messages?: LeadMessage[];
  history?: Array<{ at?: string; by?: string; what?: string }>;
  match_candidates?: Array<{ listing_id: string; name: string; score?: number }>;
}
interface OutboxJob {
  id: string; lead_id?: string; platform?: string; text?: string; status: string; status_label?: string;
  created_by?: string; created_at?: string; updated_at?: string; message?: string;
  send_at?: string | null;   // while queued: end of the server's 20 s undo window (or the next AI retry)
  // AI-unblock: put back in the queue because Claude was unavailable; it
  // retries by itself and the person may copy it and send it by hand.
  ai_wait?: { kind?: string | null; tries?: number; max_tries?: number } | null;
}
interface LeadsPayload {
  as_of?: string;
  leads: LeadSummary[];
  kpis?: { waiting?: number; breached?: number; new_today?: number; new_7d?: number; median_first_response_min_7d?: number | null; by_platform?: Record<string, number> };
  platforms?: Record<string, PlatformBrief>;
  agent?: AgentBrief;
  properties?: Array<{ listing_id: string; name: string }>;
  sla_min?: Record<string, number>;
  // replies_wait: a reply sent now waits and goes out by itself (false: key
  // rejected / not set up — replies fail at once and must be sent by hand)
  ai_outage?: { kind?: string | null; reason?: string | null; since?: string | null; replies_wait?: boolean } | null;
}
interface ReqResult { ok: boolean; status: number; data: any; error: string; }
interface Note { text: string; tone: Tone; }
interface FocusMark { key: string; start: number | null; end: number | null; }

export interface LeadsInboxOptions {
  openAgentTab?: () => void;       // switch MtrPage to the "Leads agent" tab
  openPipelineTab?: (mtrLeadId?: string) => void;    // switch MtrPage to the "Pipeline" (kanban) tab — R114: and open that renter's card
  onPipelineAdded?: () => void;    // a lead was added to the kanban — MtrPage refreshes its copy
}

const PLATFORMS: PlatformId[] = ['zillow', 'ff', 'avail'];
const PLAT_DEFAULTS: Record<string, { short: string; label: string; prose: string }> = {
  zillow: { short: 'Zillow', label: 'Zillow Rental Manager', prose: 'Zillow' },
  ff: { short: 'FF', label: 'Furnished Finder', prose: 'Furnished Finder' },
  avail: { short: 'Avail', label: 'Avail', prose: 'Avail' },
};
const NEEDS_PERSON = ['login', 'two_factor', 'challenge'];
const PENDING_JOB = ['queued', 'dispatched', 'in_progress', 'filled', 'clicked'];
const PROBLEM_JOB = ['uncertain', 'failed', 'blocked'];
const STATUS_LABEL: Record<string, string> = { open: 'Open', handled: 'Handled', archived: 'Archived', spam: 'Spam' };
const INTENT_LABEL: Record<string, string> = {
  inquiry: 'Inquiry', tour_request: 'Tour request', application: 'Application',
  booking_request: 'Booking request', tenant_lead: 'Tenant lead', other: 'Other',
};
const MATCH_LABEL: Record<string, string> = { auto: 'matched automatically', alias: 'matched by a saved alias', manual: 'set by hand' };
const FACTS: Array<[string, string]> = [
  ['move_in', 'Move-in'], ['move_out', 'Move-out'], ['lease_months', 'Lease'], ['occupants', 'Occupants'],
  ['pets', 'Pets'], ['budget', 'Budget'], ['income', 'Income'], ['credit', 'Credit'],
  ['employment', 'Employment'], ['profession', 'Profession'], ['notes', 'Notes'],
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export class LeadsInbox {
  private opts: LeadsInboxOptions;
  private host: HTMLElement | null = null;
  private rootEl: HTMLElement | null = null;
  private timer: any = null;
  private cdTimer: any = null;        // 1 s tick for "Sending in 12s" undo countdowns
  private sendAtTimer: any = null;    // one-shot refetch just after a reply's send_at
  private ticking = false;

  // list + filters
  private data: LeadsPayload | null = null;
  private listLoading = false;
  private listError = '';
  private listSeq = 0;
  private view: View = 'open';
  private dataView: View = 'open';        // the view the loaded rows belong to
  private platFilter: '' | PlatformId = '';
  private waitingOnly = false;
  private mineOnly = false;
  private listScroll = 0;

  // open lead
  private selectedId: string | null = null;
  private detail: { lead: LeadDetail; replies: OutboxJob[] } | null = null;
  private detailLoading = false;
  private detailError = '';
  private detailSeq = 0;
  private pendingScroll = false;          // jump the thread to newest only after a real open/send
  private profileOpen = false;            // details drawer (narrow screens)
  private historyOpen = false;

  // composer — drafts are per lead so switching leads never loses text
  private drafts = new Map<string, string>();
  private aiInstr = '';
  private cnotes = new Map<string, Note>();        // composer note per lead (async results land on the right lead)
  private draftKeys = new Map<string, string>();   // idempotency key per draft (client_key) — new only after success / clear / replace
  private verifying: string | null = null;         // lead whose ambiguous send (network / 5xx) is being checked
  private sending = false;
  // AI-unblock: AI drafts in flight, per lead. A slow / out-of-credits AI must
  // never lock the composer: typing, Send and Add note stay usable, and a
  // draft that lands after the person typed or sent is dropped.
  private drafting = new Map<string, { ctl: AbortController; base: string; touched: boolean; why: '' | 'cancel' | 'timeout' }>();
  private noting = false;
  private statusBusy = false;

  // profile pane
  private profileEdit: { field: 'name' | 'email' | 'phone'; value: string } | null = null;
  private assigneeDraft: string | null = null;
  private pnote: (Note & { lead: string }) | null = null;
  private profileBusy = false;
  private pipelineBusy = false;
  private people: Array<{ email: string; full_name: string }> | null = null;
  private peopleLoading = false;

  constructor(arg?: HTMLElement | LeadsInboxOptions) {
    if (arg && typeof (arg as HTMLElement).nodeType === 'number') { this.host = arg as HTMLElement; this.opts = {}; }
    else this.opts = (arg as LeadsInboxOptions) || {};
  }

  // ── lifecycle ─────────────────────────────────────────────────────────────
  async mount(host: HTMLElement): Promise<void> {
    this.stopPolling();
    this.host = host;
    host.innerHTML = '<div class="lx"></div>';
    const root = host.querySelector<HTMLElement>('.lx');
    if (!root) return;
    this.rootEl = root;
    this.bindRoot(root);
    this.draw();
    this.startPolling();
    void this.loadPeople();
    await this.loadList(this.data ? 'quiet' : 'full');
    if (this.selectedId && this.rootEl === root && root.isConnected) {
      await this.loadDetail(this.selectedId, !!this.detailFor(this.selectedId));
    }
  }

  // App's 15-min auto-refresh asks first: veto while a lead is open or any
  // draft holds text (a re-mount would not lose it, but it would yank the
  // reader's scroll and focus — the R37 class).
  suspendAutoRefresh(): boolean {
    if (this.selectedId) return true;
    for (const v of this.drafts.values()) if (v.trim()) return true;
    return !!this.profileEdit;
  }

  private startPolling(): void {
    this.stopPolling();
    this.timer = setInterval(() => { void this.tick(); }, POLL_MS);
  }
  private stopPolling(): void {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    if (this.cdTimer) { clearInterval(this.cdTimer); this.cdTimer = null; }
    if (this.sendAtTimer) { clearTimeout(this.sendAtTimer); this.sendAtTimer = null; }
  }

  // ── undo-window countdowns ────────────────────────────────────────────────
  // A queued reply is held server-side until send_at (20 s undo window). The
  // bubble counts down locally; once send_at passes it reads "Waiting for the
  // agent", and the lead is refetched just after so it moves on to
  // "Sending…" / "Sent" without waiting for the 20 s poll.
  private syncCountdown(): void {
    const root = this.rootEl;
    const has = !!(root && root.isConnected && root.querySelector('[data-lx-countdown]'));
    if (has && !this.cdTimer) this.cdTimer = setInterval(() => this.tickCountdown(), 1000);
    else if (!has && this.cdTimer) { clearInterval(this.cdTimer); this.cdTimer = null; }
    this.scheduleSendAtRefetch();
  }
  private tickCountdown(): void {
    const root = this.rootEl;
    const els = root && root.isConnected ? root.querySelectorAll<HTMLElement>('[data-lx-countdown]') : null;
    if (!els || !els.length) { if (this.cdTimer) { clearInterval(this.cdTimer); this.cdTimer = null; } return; }
    const now = Date.now();
    els.forEach(el => {
      const secs = Math.ceil((Number(el.dataset.lxCountdown) - now) / 1000);
      if (secs > 0) el.textContent = `Sending in ${secs}s`;
      else { el.textContent = 'Waiting for the agent'; el.removeAttribute('data-lx-countdown'); }
    });
  }
  private scheduleSendAtRefetch(): void {
    if (this.sendAtTimer) { clearTimeout(this.sendAtTimer); this.sendAtTimer = null; }
    const id = this.selectedId;
    const d = this.detailFor(id);
    if (!id || !d || !this.rootEl || !this.rootEl.isConnected) return;
    const now = Date.now();
    const pend = (d.replies || []).filter(j => j && PENDING_JOB.includes(j.status));
    if (!pend.length) return;
    const future = pend.map(j => (j.status === 'queued' ? this.ts(j.send_at) : 0)).filter(t => t > now).sort((a, b) => a - b)[0];
    // just after the next undo window closes; otherwise follow an in-flight
    // send closely for a couple of minutes, then leave it to the 20 s poll
    const recent = pend.some(j => now - (this.ts(j.send_at) || this.ts(j.updated_at) || this.ts(j.created_at)) < 120000);
    const wait = future ? future - now + 1500 : recent ? 4000 : 0;
    if (!wait) return;
    this.sendAtTimer = setTimeout(() => {
      this.sendAtTimer = null;
      if (!this.rootEl || !this.rootEl.isConnected || this.selectedId !== id) return;
      void this.loadDetail(id, true).then(() => { if (this.selectedId === id && !this.sendAtTimer) this.scheduleSendAtRefetch(); });
    }, wait);
  }

  private async tick(): Promise<void> {
    const root = this.rootEl;
    // Self-terminate once this root is swapped out (tab switch / navigation).
    if (!root || !root.isConnected) { this.stopPolling(); return; }
    if (this.ticking || this.sending || document.hidden) return;
    this.ticking = true;
    try {
      await this.loadList('quiet');
      if (this.selectedId && this.rootEl === root && !this.typingNow()) await this.loadDetail(this.selectedId, true);
    } catch (e) {
      console.error('[leads] poll failed', e);
    } finally {
      this.ticking = false;
    }
  }

  // ── helpers ───────────────────────────────────────────────────────────────
  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }
  private n(v: any): number { const x = Number(v); return Number.isFinite(x) ? x : 0; }
  private numTxt(v: any): string { const x = Number(v); return v != null && Number.isFinite(x) ? String(Math.round(x)) : '—'; }
  private ts(s: any): number { const t = Date.parse(String(s || '')); return Number.isFinite(t) ? t : 0; }
  private safeUrl(u: any): string {
    if (typeof u !== 'string' || !u.trim()) return '';
    try { const x = new URL(u.trim()); return (x.protocol === 'https:' || x.protocol === 'http:') ? x.href : ''; } catch { return ''; }
  }
  private myEmail(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.email || '').trim().toLowerCase(); } catch { return ''; }
  }
  private durMin(min: number): string {
    const m = Math.max(0, Math.round(Number(min) || 0));
    if (m < 60) return `${m}m`;
    if (m < 1440) { const h = Math.floor(m / 60), r = m % 60; return r && h < 10 ? `${h}h ${r}m` : `${h}h`; }
    const d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60);
    return h && d < 7 ? `${d}d ${h}h` : `${d}d`;
  }
  // compact list time: now / 5m / 3h / 2d / Sep 3
  private rel(s: any): string {
    const t = this.ts(s); if (!t) return '';
    const m = (Date.now() - t) / 60000;
    if (m < 1) return 'now';
    if (m < 60) return `${Math.floor(m)}m`;
    if (m < 1440) return `${Math.floor(m / 60)}h`;
    if (m < 10080) return `${Math.floor(m / 1440)}d`;
    return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  private ago(s: any): string {
    const t = this.ts(s); if (!t) return '';
    const m = (Date.now() - t) / 60000;
    if (m < 1) return 'just now';
    if (m < 60) return `${Math.floor(m)}m ago`;
    if (m < 1440) return `${Math.floor(m / 60)}h ago`;
    if (m < 10080) return `${Math.floor(m / 1440)}d ago`;
    return `on ${new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }
  private fmtT(s: any): string {
    const t = this.ts(s); if (!t) return '';
    const d = new Date(t), now = new Date();
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (d.toDateString() === now.toDateString()) return time;
    const opts: Intl.DateTimeFormatOptions = d.getFullYear() === now.getFullYear() ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' };
    return `${d.toLocaleDateString('en-US', opts)}, ${time}`;
  }
  private platShort(pid: string): string {
    const p = this.data?.platforms?.[pid];
    return String((p && p.short) || PLAT_DEFAULTS[pid]?.short || pid || 'Platform');
  }
  private platProse(pid: string): string {
    return PLAT_DEFAULTS[pid]?.prose || String(this.data?.platforms?.[pid]?.label || pid || 'the platform');
  }
  private platChip(pid: string): string {
    const cls = PLATFORMS.includes(pid as PlatformId) ? pid : 'other';
    return `<span class="lx-plat p-${cls}">${this.esc(this.platShort(pid))}</span>`;
  }
  private personName(email: string | null | undefined): string {
    if (!email) return '';
    const p = (this.people || []).find(x => x.email.toLowerCase() === String(email).toLowerCase());
    return p && p.full_name && !p.full_name.includes('@') ? p.full_name : '';
  }
  private initials(email: string): string {
    const src = this.personName(email) || String(email).split('@')[0];
    const parts = src.split(/[\s._-]+/).filter(Boolean);
    const ini = parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : (parts[0] || '?').slice(0, 2);
    return ini.toUpperCase();
  }
  private shortWho(s: any): string { const v = String(s || ''); return this.personName(v) || v.split('@')[0]; }
  private isBreached(l: LeadSummary): boolean {
    if (!l.waiting) return false;
    if (l.breached) return true;
    const due = this.ts(l.due_at);
    return !!due && due <= Date.now();
  }
  private detailFor(id: string | null): { lead: LeadDetail; replies: OutboxJob[] } | null {
    return id && this.detail && this.detail.lead && this.detail.lead.id === id ? this.detail : null;
  }
  private curLead(): LeadDetail | LeadSummary | null {
    if (!this.selectedId) return null;
    const d = this.detailFor(this.selectedId);
    if (d) return d.lead;
    return (this.data?.leads || []).find(l => l.id === this.selectedId) || null;
  }
  private draftOf(id: string | null): string { return id ? (this.drafts.get(id) || '') : ''; }
  private typingNow(): boolean {
    const ae = document.activeElement as HTMLElement | null;
    return !!(ae && ae.dataset && ae.dataset.lxKey === 'draft' && this.draftOf(this.selectedId).trim());
  }

  // Never throws: { ok, status, data, error } with the server's {error} text.
  // signal: optional abort (AI drafts); an aborted call returns status -1.
  private async req(method: string, url: string, body?: any, signal?: AbortSignal): Promise<ReqResult> {
    try {
      const init: RequestInit = { method };
      if (body !== undefined) { init.headers = { 'Content-Type': 'application/json' }; init.body = JSON.stringify(body); }
      if (signal) init.signal = signal;
      const r = await fetch(url, init);
      const data = await r.json().catch(() => null);
      if (!r.ok) {
        const msg = (data && typeof data.error === 'string' && data.error) ? data.error
          : r.status === 404 ? 'Not found (HTTP 404) — the leads API may not be deployed on this server yet.'
          : r.status === 403 ? 'Not allowed (HTTP 403) — the lead inbox is for office roles.'
          : `Request failed (HTTP ${r.status}).`;
        return { ok: false, status: r.status, data, error: msg };
      }
      return { ok: true, status: r.status, data, error: '' };
    } catch {
      if (signal && signal.aborted) return { ok: false, status: -1, data: null, error: 'Cancelled.' };
      return { ok: false, status: 0, data: null, error: 'Network error — check your connection and try again.' };
    }
  }

  // ── data ──────────────────────────────────────────────────────────────────
  private async loadPeople(): Promise<void> {
    if (this.people || this.peopleLoading) return;
    this.peopleLoading = true;
    const r = await this.req('GET', '/api/tasks/assignees');
    this.peopleLoading = false;
    // tolerate failure: an empty list just means no autocomplete
    const people: Array<{ email: string; full_name: string }> = r.ok && r.data && Array.isArray(r.data.people)
      ? r.data.people.filter((p: any) => p && p.email).map((p: any) => ({ email: String(p.email), full_name: String(p.full_name || p.email) }))
      : [];
    this.people = people;
    if (people.length) {
      this.patchRows();
      if (this.selectedId && !this.profileFocused()) this.patchProfile();
    }
  }

  private async loadList(mode: 'full' | 'quiet'): Promise<void> {
    const seq = ++this.listSeq;
    const view = this.view;
    const r = await this.req('GET', `${API}?view=${encodeURIComponent(view)}`);
    if (seq !== this.listSeq) return;   // superseded (filter changed mid-flight)
    this.listLoading = false;
    if (!r.ok || !r.data || !Array.isArray(r.data.leads)) {
      this.listError = r.ok ? 'The server sent an unexpected answer.' : r.error;
      if (!this.data) this.draw();
      else { this.patchAgent(); this.patchRows(); }
      return;
    }
    const wasBig = this.isBigEmptyRendered();
    this.data = r.data as LeadsPayload;
    this.dataView = view;
    this.listError = '';
    if (mode === 'full' || !this.rootEl || !this.rootEl.querySelector('[data-lx-region="rows"]') || wasBig !== this.bigEmpty()) { this.draw(); return; }
    this.patch('kpis', this.kpisHtml());
    this.patchAgent();
    this.patchList();
    this.patch('hint', this.hintHtml());
    this.updateComposerButtons();
  }

  // Returns the fetched detail (null on failure) even when it arrives after
  // the user moved to another lead — it is only APPLIED while that lead is open.
  private async loadDetail(id: string, quiet: boolean, force = false): Promise<{ lead: LeadDetail; replies: OutboxJob[] } | null> {
    const seq = ++this.detailSeq;
    if (!quiet) { this.detailLoading = true; this.detailError = ''; }
    const r = await this.req('GET', `${API}/${encodeURIComponent(id)}`);
    const next = r.ok && r.data && r.data.lead
      ? { lead: r.data.lead as LeadDetail, replies: Array.isArray(r.data.replies) ? r.data.replies as OutboxJob[] : [] }
      : null;
    if (seq !== this.detailSeq || this.selectedId !== id) return next;
    this.detailLoading = false;
    if (!next) {
      // a quiet refresh that fails keeps the thread we already have
      if (!quiet || !this.detailFor(id)) { this.detailError = r.ok ? 'The server sent an unexpected answer.' : r.error; this.patchThread(); }
      return null;
    }
    const prev = this.detailFor(id);
    const threadChanged = force || !prev || this.threadSig(prev) !== this.threadSig(next);
    const profChanged = force || !prev || this.profSig(prev.lead) !== this.profSig(next.lead);
    const compChanged = force || !prev || prev.lead.status !== next.lead.status || prev.lead.platform !== next.lead.platform;
    this.detail = next;
    this.detailError = '';
    this.mergeSummary(next.lead);
    if (threadChanged) this.patchThread();
    else if (profChanged) this.patch('head', this.headHtml());
    if (profChanged) { if (!this.profileFocused()) this.patchProfile(); this.patchRows(); }
    if (compChanged) this.patchComposer();
    this.syncCountdown();
    return next;
  }

  private threadSig(d: { lead: LeadDetail; replies: OutboxJob[] }): string {
    const msgs = d.lead.messages || [];
    const last = msgs[msgs.length - 1];
    return [msgs.length, last ? `${last.id || ''}:${last.at || ''}` : '', d.lead.partial ? 1 : 0,
      (d.replies || []).map(j => `${j.id}:${j.status}`).join(',')].join('|');
  }
  private profSig(l: LeadDetail): string {
    return JSON.stringify([l.name, l.email, l.phone, l.listing_id, l.listing_name, l.match_via, l.assignee, l.status,
      l.mtr_lead_id, l.profile, l.tags, l.intent, l.duplicate_of, (l.history || []).length, l.first_response_at,
      l.match_candidates, l.thread_url, l.waiting, l.due_at]);
  }
  // Keep the list row in step with a fresher detail (without the heavy fields).
  private mergeSummary(lead: LeadDetail): void {
    if (!this.data) return;
    const i = this.data.leads.findIndex(l => l.id === lead.id);
    if (i < 0) return;
    const { messages, history, profile, match_candidates, ...summary } = lead;
    void messages; void history; void profile; void match_candidates;
    this.data.leads[i] = { ...this.data.leads[i], ...summary };
  }
  private applyLead(lead: LeadDetail | null | undefined): void {
    if (!lead || !lead.id) return;
    const prev = this.detailFor(lead.id);
    if (prev) prev.lead = lead;
    else if (this.selectedId === lead.id) this.detail = { lead, replies: [] };
    this.mergeSummary(lead);
  }

  private visibleLeads(): LeadSummary[] {
    const me = this.myEmail();
    let rows = (this.data?.leads || []).slice();
    if (this.platFilter) rows = rows.filter(l => l.platform === this.platFilter);
    if (this.waitingOnly) rows = rows.filter(l => l.waiting);
    if (this.mineOnly) rows = rows.filter(l => !!me && String(l.assignee || '').toLowerCase() === me);
    const FAR = 8.64e15;
    const rank = (l: LeadSummary) => (l.waiting ? (this.isBreached(l) ? 0 : 1) : 2);
    rows.sort((a, b) => {
      const ra = rank(a), rb = rank(b);
      if (ra !== rb) return ra - rb;
      if (ra < 2) return (this.ts(a.waiting_since) || FAR) - (this.ts(b.waiting_since) || FAR);   // longest-waiting first
      return (this.ts(b.last_message_at) || this.ts(b.updated_at)) - (this.ts(a.last_message_at) || this.ts(a.updated_at));
    });
    return rows;
  }
  private filtersActive(): boolean { return !!(this.platFilter || this.waitingOnly || this.mineOnly); }
  private neverSynced(): boolean {
    const p = this.data?.platforms || {};
    return !Object.keys(p).some(k => !!(p[k] && p[k].last_sync_at));
  }
  // The big "connect the agent" explainer replaces the three panes only when
  // there is genuinely nothing yet (not merely nothing matching a filter).
  private bigEmpty(): boolean {
    return !!this.data && !this.data.leads.length && !this.selectedId && this.view !== 'archived' && !this.filtersActive() && this.neverSynced();
  }
  private isBigEmptyRendered(): boolean { return !!this.rootEl && !!this.rootEl.querySelector('.lx-big'); }

  // ── rendering: shell ──────────────────────────────────────────────────────
  private draw(): void {
    const root = this.rootEl;
    if (!root || !root.isConnected) return;
    const focus = this.captureFocus(root);
    const msgsEl = root.querySelector<HTMLElement>('[data-lx-region="msgs"]');
    const msgsPrev = msgsEl ? { lead: msgsEl.dataset.lxLead || '', top: msgsEl.scrollTop, atBottom: msgsEl.scrollHeight - msgsEl.scrollTop - msgsEl.clientHeight < 80 } : null;

    if (!this.data) {
      root.innerHTML = this.css() + (this.listError
        ? `<div class="lx-big"><div class="lx-big-ic">⚠️</div><h3>Couldn't load the lead inbox</h3><p>${this.esc(this.listError)}</p><button type="button" class="lx-btn" data-lx-act="retry-list">Retry</button></div>`
        : `<div style="padding:48px; text-align:center;">${LoadingSpinner.render()}</div>`);
      return;
    }

    const sel = !!this.selectedId;
    const body = this.bigEmpty() ? this.bigEmptyHtml() : `
      <div class="lx-wrap${sel ? ' has-sel' : ''}${sel && this.profileOpen ? ' prof-open' : ''}" data-lx-wrap>
        <aside class="lx-list">
          <div class="lx-filters" data-lx-region="filters">${this.filtersHtml()}</div>
          <div class="lx-rows" data-lx-region="rows">${this.rowsHtml()}</div>
        </aside>
        <section class="lx-main">${sel ? `
          <div data-lx-region="head">${this.headHtml()}</div>
          <div class="lx-msgs" data-lx-region="msgs" data-lx-lead="${this.esc(this.selectedId)}">${this.msgsHtml()}</div>
          <div data-lx-region="hint">${this.hintHtml()}</div>
          <div data-lx-region="composer">${this.composerHtml()}</div>` : `
          <div class="lx-pick"><div class="lx-pick-ic">💬</div><div>Pick a lead to read the conversation and reply.</div><div class="lx-faint" style="margin-top:4px;">Waiting and overdue leads are at the top of the list.</div></div>`}
        </section>
        ${sel ? `<aside class="lx-profile" data-lx-region="profile">${this.profileHtml()}</aside>` : ''}
      </div>`;

    root.innerHTML = `${this.css()}
      <div class="lx-kpis" data-lx-region="kpis">${this.kpisHtml()}</div>
      <div data-lx-region="agent">${this.agentHtml()}</div>
      ${body}`;

    const rows = root.querySelector<HTMLElement>('.lx-rows');
    if (rows) rows.scrollTop = this.listScroll;
    const msgs = root.querySelector<HTMLElement>('[data-lx-region="msgs"]');
    if (msgs) {
      const same = msgsPrev && msgsPrev.lead === (this.selectedId || '');
      if ((this.pendingScroll && this.detailFor(this.selectedId)) || (same && msgsPrev!.atBottom) || !msgsPrev) {
        msgs.scrollTop = msgs.scrollHeight;
        if (this.detailFor(this.selectedId)) this.pendingScroll = false;
      } else if (same) msgs.scrollTop = msgsPrev!.top;
      else msgs.scrollTop = msgs.scrollHeight;
    }
    this.restoreFocus(root, focus);
    this.syncCountdown();
  }

  // Partial repaint of one region, keeping focus + caret of a keyed input.
  private patch(region: string, html: string): HTMLElement | null {
    const root = this.rootEl;
    if (!root || !root.isConnected) return null;
    const el = root.querySelector<HTMLElement>(`[data-lx-region="${region}"]`);
    if (!el) return null;
    const f = this.captureFocus(el);
    el.innerHTML = html;
    this.restoreFocus(el, f);
    return el;
  }
  private captureFocus(scope: HTMLElement): FocusMark | null {
    const ae = document.activeElement as HTMLInputElement | null;
    if (!ae || !scope.contains(ae) || !ae.dataset || !ae.dataset.lxKey) return null;
    let start: number | null = null, end: number | null = null;
    try { start = ae.selectionStart ?? null; end = ae.selectionEnd ?? null; } catch { /* select / number */ }
    return { key: ae.dataset.lxKey, start, end };
  }
  private restoreFocus(scope: HTMLElement, f: FocusMark | null): void {
    if (!f) return;
    const el = scope.querySelector<HTMLInputElement>(`[data-lx-key="${f.key}"]`);
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch { el.focus(); }
    if (f.start != null) { try { el.setSelectionRange(f.start, f.end ?? f.start); } catch { /* not a text control */ } }
  }
  private profileFocused(): boolean {
    const el = this.rootEl?.querySelector('[data-lx-region="profile"]');
    const ae = document.activeElement;
    return !!(this.profileEdit || this.profileBusy || (el && ae && el.contains(ae) && /^(INPUT|SELECT|TEXTAREA)$/.test(ae.tagName)));
  }

  private patchList(): void {
    const root = this.rootEl;
    if (!root || !root.querySelector('[data-lx-region="rows"]')) { this.draw(); return; }
    this.patch('filters', this.filtersHtml());
    this.patchRows();
  }
  private patchRows(): void {
    const el = this.patch('rows', this.rowsHtml());
    if (el) el.scrollTop = this.listScroll;
  }
  private patchAgent(): void { this.patch('agent', this.agentHtml()); }
  private patchComposer(): void { this.patch('composer', this.composerHtml()); }
  private patchProfile(): void { this.patch('profile', this.profileHtml()); }
  private patchThread(): void {
    const root = this.rootEl;
    if (!root || !root.isConnected) return;
    const box = root.querySelector<HTMLElement>('[data-lx-region="msgs"]');
    if (!box || box.dataset.lxLead !== (this.selectedId || '')) { if (this.selectedId) this.draw(); return; }
    this.patch('head', this.headHtml());
    const atBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 80;
    const top = box.scrollTop;
    box.innerHTML = this.msgsHtml();
    if (this.pendingScroll || atBottom) {
      box.scrollTop = box.scrollHeight;
      if (this.detailFor(this.selectedId)) this.pendingScroll = false;
    } else box.scrollTop = top;
    this.syncCountdown();
  }

  // ── rendering: KPIs + agent status ────────────────────────────────────────
  private kpisHtml(): string {
    const k = this.data?.kpis || {};
    const sla = this.data?.sla_min || {};
    const slaTxt = PLATFORMS.filter(p => sla[p] != null && Number.isFinite(Number(sla[p]))).map(p => `${this.platShort(p)} ${this.durMin(Number(sla[p]))}`).join(' · ');
    const med = k.median_first_response_min_7d;
    const tile = (label: string, value: string, cap: string, tone = '') =>
      `<div class="lx-kpi ${tone}"><div class="lx-kpi-l">${label}</div><div class="lx-kpi-v">${value}</div><div class="lx-kpi-c" title="${cap}">${cap}</div></div>`;
    return [
      tile('Waiting on us', this.numTxt(k.waiting), 'renter wrote last, no reply yet', this.n(k.waiting) > 0 ? 'warn' : ''),
      tile('Past response target', this.numTxt(k.breached), slaTxt ? `targets: ${this.esc(slaTxt)}` : 'reply later than the target', this.n(k.breached) > 0 ? 'bad' : ''),
      tile('New today', this.numTxt(k.new_today), 'first message today'),
      tile('New · 7 days', this.numTxt(k.new_7d), 'first message in the last 7 days'),
      tile('Median first response', med == null || !Number.isFinite(Number(med)) ? '—' : this.esc(this.durMin(Number(med))), 'last 7 days · time to our first reply'),
    ].join('');
  }

  private agentHtml(): string {
    const d = this.data;
    if (!d) return '';
    const a: AgentBrief = d.agent || {};
    const segs: string[] = [];
    const agentTitle = [a.host, a.version ? `v${a.version}` : ''].filter(Boolean).join(' · ');
    segs.push(`<span class="lx-ag" title="${this.esc(agentTitle)}"><span class="lx-dot ${a.online ? 'on' : ''}"></span><b>${a.online ? 'Agent online' : 'Agent offline'}</b>${!a.online ? (a.last_seen_at ? ` · last seen ${this.esc(this.ago(a.last_seen_at))}` : ' · never connected') : ''}</span>`);
    const needs: string[] = [];
    for (const pid of PLATFORMS) {
      const p = d.platforms?.[pid];
      if (!p) continue;
      const st = String(p.state || '');
      let tone = 'ok', txt = '';
      if (p.enabled === false || st === 'disabled') { tone = 'off'; txt = 'off'; }
      else if (NEEDS_PERSON.includes(st)) {
        tone = 'warn';
        txt = st === 'two_factor' ? '⚠ needs a sign-in code' : st === 'challenge' ? '⚠ needs a human check' : '⚠ needs sign-in';
        needs.push(this.platShort(pid));
      }
      else if (st === 'error') { tone = 'bad'; txt = `✕ ${p.state_label || 'error'}`; }
      else if (st === 'offline') { tone = 'off'; txt = 'agent offline'; }
      else if (st === 'never_run') { tone = 'off'; txt = 'not synced yet'; }
      else if (st === 'ok') { txt = `✓ connected${p.last_sync_at ? ` · synced ${this.ago(p.last_sync_at)}` : ''}`; }
      else { tone = 'off'; txt = p.state_label || st || 'unknown'; }
      const title = [p.state_label, p.message].filter(Boolean).join(' — ');
      segs.push(`<span class="lx-sep">·</span><span class="lx-ps ${tone}" title="${this.esc(title)}"><b>${this.esc(this.platShort(pid))}</b> ${this.esc(txt)}</span>`);
    }
    const tail = `<span class="lx-status-r">${this.listError ? `<span class="lx-ps bad" title="${this.esc(this.listError)}">⚠ couldn't refresh</span>` : ''}${d.as_of ? `<span class="lx-faint">updated ${this.esc(this.ago(d.as_of))}</span>` : ''}<button type="button" class="lx-link" data-lx-act="refresh">↻ Refresh</button><button type="button" class="lx-link" data-lx-act="agent">Leads agent →</button></span>`;
    let callout = '';
    if (needs.length) {
      const who = needs.length === 1 ? `${needs[0]} needs` : `${needs.slice(0, -1).join(', ')} and ${needs[needs.length - 1]} need`;
      callout = `<div class="lx-callout"><span>🔐 <b>${this.esc(who)} a person to sign in</b> → open the Leads agent tab and use “Open live browser”. Until then nothing new arrives from ${needs.length === 1 ? 'it' : 'them'} and replies wait.</span><button type="button" class="lx-btn sm" data-lx-act="agent">Open Leads agent</button></div>`;
    } else if (!a.online && a.last_seen_at && PLATFORMS.some(pid => d.platforms?.[pid]?.enabled)) {
      callout = `<div class="lx-callout grey"><span>The Leads agent is offline (last seen ${this.esc(this.ago(a.last_seen_at))}) — nothing new syncs and queued replies wait until it's back. Check that the agent Mac is awake.</span><button type="button" class="lx-btn sm" data-lx-act="agent">Open Leads agent</button></div>`;
    }
    // AI-unblock: say why replies are waiting while Claude is out — worded by
    // kind: only outages that make replies wait (out of credits, busy,
    // unreachable) promise automatic sending; with the key rejected or not
    // set up, a reply sent from here fails at once and must go by hand.
    if (d.ai_outage) {
      const o = d.ai_outage;
      const why = o.reason ? ` (${o.reason})` : '';
      const waits = typeof o.replies_wait === 'boolean' ? o.replies_wait : !['auth', 'config'].includes(String(o.kind || ''));
      const what = waits
        ? `Replies you send still queue and go out automatically once it's back. To send one by hand instead, press Copy reply on its bubble first (that cancels the automatic send), then paste it on the platform.`
        : `The agent can't send replies until an admin fixes this — replies sent from here won't go out. Copy your reply and send it by hand on the platform.`;
      callout += `<div class="lx-callout"><span>🤖 <b>The AI is unavailable${this.esc(why)}</b>${o.since ? ` (noticed ${this.esc(this.ago(o.since))})` : ''}. ${this.esc(what)} New messages are read once it's back; AI drafts may fail, so write replies yourself.</span></div>`;
    }
    return `<div class="lx-status">${segs.join('')}${tail}</div>${callout}`;
  }

  private bigEmptyHtml(): string {
    return `
      <div class="lx-big">
        <div class="lx-big-ic">📬</div>
        <h3>No leads yet</h3>
        <p>Inquiries from Zillow Rental Manager, Furnished Finder and Avail show up here once the <b>Leads agent</b> is connected and has synced. The agent runs on an always-on Mac, reads each platform's inbox into this one list, and types the replies you write here back into the platform.</p>
        <button type="button" class="lx-btn primary" data-lx-act="agent">Open the Leads agent tab</button>
      </div>`;
  }

  // ── rendering: list ───────────────────────────────────────────────────────
  private filtersHtml(): string {
    const all = this.data?.leads || [];
    const bp = this.data?.kpis?.by_platform || {};
    const visible = this.visibleLeads().length;
    const me = this.myEmail();
    const seg = (v: View, label: string) => `<button type="button" class="lx-seg-b${this.view === v ? ' on' : ''}" data-lx-act="view" data-lx-view="${v}">${label}</button>`;
    const chip = (pid: string, label: string, n: number | null) =>
      `<button type="button" class="lx-chip${this.platFilter === pid ? ' on' : ''}" data-lx-act="plat" data-lx-plat="${pid}">${label}${n != null ? `<span class="lx-chip-n">${n}</span>` : ''}</button>`;
    const waitingN = all.filter(l => l.waiting).length;
    const mineN = me ? all.filter(l => String(l.assignee || '').toLowerCase() === me).length : 0;
    return `
      <div class="lx-frow">
        <div class="lx-seg" role="group" aria-label="Which leads">${seg('open', 'Open')}${seg('all', 'All')}${seg('archived', 'Archived')}</div>
        <span class="lx-count">${visible} shown</span>
      </div>
      <div class="lx-chips">
        ${chip('', 'All', all.length)}
        ${PLATFORMS.map(p => chip(p, this.esc(this.platShort(p)), bp[p] != null && Number.isFinite(Number(bp[p])) ? Number(bp[p]) : all.filter(l => l.platform === p).length)).join('')}
      </div>
      <div class="lx-chips">
        <button type="button" class="lx-chip tog${this.waitingOnly ? ' on' : ''}" data-lx-act="toggle-waiting" title="Only leads where the renter wrote last">Waiting only${waitingN ? `<span class="lx-chip-n">${waitingN}</span>` : ''}</button>
        <button type="button" class="lx-chip tog${this.mineOnly ? ' on' : ''}" data-lx-act="toggle-mine" title="${me ? `Assigned to ${this.esc(me)}` : 'Your email is unknown — sign in again'}">Mine${mineN ? `<span class="lx-chip-n">${mineN}</span>` : ''}</button>
      </div>`;
  }

  private rowsHtml(): string {
    if (this.listLoading) return `<div style="padding:30px; text-align:center;">${LoadingSpinner.render()}</div>`;
    // a failed view switch must not show the previous view's rows under the new chip
    if (this.listError && this.dataView !== this.view) return `<div class="lx-empty">⚠️ Couldn't load this list — ${this.esc(this.listError)}<div style="margin-top:10px;"><button type="button" class="lx-btn sm" data-lx-act="retry-list">Retry</button></div></div>`;
    const rows = this.visibleLeads();
    if (!rows.length) {
      if (this.filtersActive()) return `<div class="lx-empty">No leads match these filters.<div style="margin-top:10px;"><button type="button" class="lx-btn sm" data-lx-act="clear-filters">Clear filters</button></div></div>`;
      if (this.view === 'archived') return `<div class="lx-empty">Nothing archived.</div>`;
      if (this.view === 'open') return `<div class="lx-empty">🎉 No open leads — you're all caught up.<div class="lx-faint" style="margin-top:4px;">New inquiries appear here within a few minutes of arriving.</div></div>`;
      return `<div class="lx-empty">No leads yet.</div>`;
    }
    return rows.map(l => this.rowHtml(l)).join('');
  }

  private problemTitle(p: string, pid: string): string {
    const name = this.platProse(pid);
    if (p === 'uncertain') return `The agent isn't sure the last reply went out — check the conversation on ${name}.`;
    if (p === 'blocked') return `The last reply was not sent — ${name} needs someone to sign in.`;
    return `The last reply was not sent — check ${name}.`;
  }

  private slaChip(l: LeadSummary): string {
    if (!l.waiting) return '';
    const due = this.ts(l.due_at);
    if (this.isBreached(l)) {
      const over = due ? (Date.now() - due) / 60000 : 0;
      return `<span class="lx-sla bad" title="${this.esc(`Past the response target${due ? ` — reply was due ${this.fmtT(l.due_at)}` : ''}`)}">overdue${over >= 1 ? ` ${this.esc(this.durMin(over))}` : ''}</span>`;
    }
    if (due) {
      const left = (due - Date.now()) / 60000;
      return `<span class="lx-sla warn" title="${this.esc(`Reply due ${this.fmtT(l.due_at)}`)}">${left < 1 ? 'due now' : `due in ${this.esc(this.durMin(left))}`}</span>`;
    }
    const since = this.ts(l.waiting_since);
    return `<span class="lx-sla warn">waiting${since ? ` ${this.esc(this.durMin((Date.now() - since) / 60000))}` : ''}</span>`;
  }

  private rowHtml(l: LeadSummary): string {
    const on = l.id === this.selectedId;
    const breached = this.isBreached(l);
    const prop = l.listing_name || l.property_text || '';
    const badges: string[] = [];
    const sla = this.slaChip(l);
    if (sla) badges.push(sla);
    if (l.reply_ai_wait) badges.push(`<span class="lx-badge q" title="A reply is waiting because the AI is unavailable — it sends automatically once the AI is back. To send it by hand, use Copy reply on it first (that cancels the automatic send).">⏳ waiting on AI</span>`);
    else if (this.n(l.pending_replies) > 0) badges.push(`<span class="lx-badge q" title="A reply is queued for the agent to send">⏳ reply queued</span>`);
    if (l.reply_problem) badges.push(`<span class="lx-badge warn" title="${this.esc(this.problemTitle(String(l.reply_problem), l.platform))}">⚠ check ${this.esc(this.platShort(l.platform))}</span>`);
    if (l.status && l.status !== 'open') badges.push(`<span class="lx-badge">${this.esc(STATUS_LABEL[l.status] || l.status)}</span>`);
    if (l.partial) badges.push(`<span class="lx-badge dim" title="Only the inbox preview so far — the agent hasn't read the full thread yet">partial</span>`);
    if (this.draftOf(l.id).trim()) badges.push(`<span class="lx-badge draft" title="You have an unsent draft">✎ draft</span>`);
    const av = l.assignee ? `<span class="lx-av" title="${this.esc(`Assigned to ${this.personName(l.assignee) || l.assignee}`)}">${this.esc(this.initials(l.assignee))}</span>` : '';
    const when = l.last_message_at || l.updated_at;
    return `
      <button type="button" class="lx-row${on ? ' on' : ''}${l.waiting ? ' waiting' : ''}${breached ? ' breached' : ''}${l.partial ? ' partial' : ''}" data-lx-act="open" data-lx-id="${this.esc(l.id)}">
        <div class="lx-row-top"><span class="lx-row-name">${l.waiting ? '<span class="lx-wdot"></span>' : ''}${this.esc(l.name || 'Unknown renter')}</span><span class="lx-row-time" title="${this.esc(this.fmtT(when))}">${this.esc(this.rel(when))}</span></div>
        <div class="lx-row-sub">${this.platChip(l.platform)}<span class="lx-row-prop">${prop ? this.esc(prop) : '<span class="lx-faint">property not matched</span>'}</span></div>
        ${l.preview ? `<div class="lx-row-prev">${this.esc(l.preview)}</div>` : ''}
        ${badges.length || av ? `<div class="lx-row-foot">${badges.join('')}${av}</div>` : ''}
      </button>`;
  }

  // ── rendering: thread ─────────────────────────────────────────────────────
  private headHtml(): string {
    const L = this.curLead();
    const back = `<button type="button" class="lx-btn sm lx-back" data-lx-act="back" aria-label="Back to the list">‹ Back</button>`;
    if (!L) return `<div class="lx-head">${back}<div class="lx-head-t"><div class="lx-meta">Loading…</div></div></div>`;
    const url = this.safeUrl(L.thread_url);
    const prop = L.listing_name || L.property_text || '';
    const sub: string[] = [prop ? this.esc(prop) : '<span class="lx-faint">property not matched</span>'];
    if (L.intent && L.intent !== 'inquiry') sub.push(this.esc(INTENT_LABEL[L.intent] || L.intent));
    if (L.assignee) sub.push(`assigned to ${this.esc(this.personName(L.assignee) || L.assignee)}`);
    return `
      <div class="lx-head">
        ${back}
        <div class="lx-head-t">
          <div class="lx-head-name"><span class="lx-head-nm">${this.esc(L.name || 'Unknown renter')}</span>${this.platChip(L.platform)}${L.status && L.status !== 'open' ? `<span class="lx-badge">${this.esc(STATUS_LABEL[L.status] || L.status)}</span>` : ''}${this.slaChip(L)}</div>
          <div class="lx-head-sub">${sub.join(' · ')}</div>
        </div>
        ${url ? `<a class="lx-btn sm" href="${this.esc(url)}" target="_blank" rel="noopener noreferrer" title="${this.esc(`Open this conversation on ${this.platProse(L.platform)}`)}">Open in ${this.esc(this.platShort(L.platform))} ↗</a>` : ''}
        <button type="button" class="lx-btn sm lx-prof-toggle" data-lx-act="toggle-profile">Details</button>
      </div>`;
  }

  private msgsHtml(): string {
    const d = this.detailFor(this.selectedId);
    if (!d) {
      if (this.detailError) return `<div class="lx-empty">⚠️ Couldn't load this conversation — ${this.esc(this.detailError)}<div style="margin-top:10px;"><button type="button" class="lx-btn sm" data-lx-act="retry-detail">Retry</button></div></div>`;
      return `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`;
    }
    const L = d.lead;
    const msgs = Array.isArray(L.messages) ? L.messages : [];
    const parts: string[] = [];
    if (L.partial) parts.push(`<div class="lx-sys">Only the inbox preview has been read so far — the full conversation appears after the agent's next sync.</div>`);
    for (const m of msgs) parts.push(this.bubbleHtml(m, L));
    // queued / in-flight / problem replies (a job whose message already landed is skipped)
    const landed = new Set(msgs.map(m => m.job_id).filter(Boolean) as string[]);
    const jobs = (d.replies || []).slice().reverse();   // server sends newest first
    let shownJobs = 0;
    for (const j of jobs) {
      if (!j || landed.has(j.id)) continue;
      if (PENDING_JOB.includes(j.status)) { parts.push(this.pendingHtml(j, L)); shownJobs++; }
      else if (PROBLEM_JOB.includes(j.status)) { parts.push(this.problemHtml(j, L)); shownJobs++; }
    }
    if (!msgs.length && !shownJobs && !L.partial) parts.push(`<div class="lx-empty">No messages in this conversation yet.</div>`);
    return parts.join('');
  }

  private bubbleHtml(m: LeadMessage, L: LeadSummary): string {
    const when = this.fmtT(m.at) || m.at_text || '';
    const text = `<div class="lx-b-text">${this.esc(m.text)}</div>`;
    const title = m.at ? ` title="${this.esc(new Date(this.ts(m.at)).toLocaleString('en-US'))}"` : '';
    if (m.direction === 'system') return `<div class="lx-sys"${title}>${this.esc(m.text)}${when ? ` · ${this.esc(when)}` : ''}</div>`;
    if (m.direction === 'note') {
      return `<div class="lx-note"><div class="lx-note-h"><span>🔒 Internal note</span><span class="lx-note-by"${title}>${this.esc(this.shortWho(m.author || m.by))}${when ? ` · ${this.esc(when)}` : ''}</span></div>${text}</div>`;
    }
    const out = m.direction === 'out';
    let who = out ? (m.author || 'West Coast Homestays') : (m.author || L.name || 'Renter');
    if (out && m.via === 'dash' && m.by) who = `${who} · sent by ${this.shortWho(m.by)}`;
    return `<div class="lx-b ${out ? 'out' : 'in'}">${text}<div class="lx-b-meta"${title}>${this.esc(who)}${when ? ` · ${this.esc(when)}` : ''}</div></div>`;
  }

  // AI-unblock: the typed reply is never stuck behind the AI — it can always
  // be copied and sent by hand on the platform.
  private copyBtn(j: OutboxJob): string {
    return `<button type="button" class="lx-link" data-lx-act="copy-reply" data-lx-job="${this.esc(j.id)}" title="Copy this reply to paste it into the platform yourself">Copy reply</button>`;
  }

  private pendingHtml(j: OutboxJob, L: LeadSummary): string {
    const queued = j.status === 'queued';
    if (queued && j.ai_wait) {
      // AI-unblock: waiting out an AI outage — no undo countdown; the reason,
      // the next automatic try, Copy reply and Cancel. Copy reply here cancels
      // the job BEFORE copying: "send by hand, then Cancel" lost the race when
      // the AI came back (the reply is released within one poll) and the
      // renter got it twice.
      const next = this.ts(j.send_at) > Date.now() ? ` Next try ${this.fmtT(j.send_at)}.` : '';
      const why = j.message || `Waiting — AI unavailable. It will send automatically once the AI is back. To send it by hand instead, press Copy reply (that cancels the automatic send first), then paste it on ${this.platProse(L.platform)}.`;
      const copy = `<button type="button" class="lx-link" data-lx-act="copy-cancel-reply" data-lx-job="${this.esc(j.id)}" title="Cancel the automatic send, then copy this reply to paste it into the platform yourself">Copy reply</button>`;
      return `<div class="lx-b out pending"><div class="lx-b-text">${this.esc(j.text)}</div><div class="lx-b-warn">⏳ ${this.esc(why)}${this.esc(next)}</div><div class="lx-b-meta">${copy}<span>·</span><button type="button" class="lx-link" data-lx-act="cancel-reply" data-lx-job="${this.esc(j.id)}" title="Stop the agent from sending this reply — press this BEFORE sending it by hand">Cancel</button>${j.created_by ? `<span>· ${this.esc(this.shortWho(j.created_by))}</span>` : ''}</div></div>`;
    }
    const sendAt = queued ? this.ts(j.send_at) : 0;
    const secs = sendAt ? Math.ceil((sendAt - Date.now()) / 1000) : 0;
    // in the undo window: a live "Sending in 12s" (ticked by syncCountdown)
    const label = secs > 0
      ? `<span data-lx-countdown="${sendAt}">Sending in ${secs}s</span>`
      : `<span>${this.esc(queued ? 'Waiting for the agent' : (j.status_label || 'Sending…'))}</span>`;
    // AI-unblock: the server also cancels a reply the agent has picked up but
    // not typed yet ('dispatched' / 'in_progress'), e.g. one released the
    // moment the AI came back while someone was about to send it by hand.
    const cancelable = queued || j.status === 'dispatched' || j.status === 'in_progress';
    const cancel = cancelable ? `<button type="button" class="lx-link" data-lx-act="cancel-reply" data-lx-job="${this.esc(j.id)}" title="Stop this reply before the agent types it into the platform">Cancel</button>` : '';
    return `<div class="lx-b out pending"><div class="lx-b-text">${this.esc(j.text)}</div><div class="lx-b-meta"><span>⏳</span>${label}${cancel ? `<span>·</span>${cancel}` : ''}${j.created_by ? `<span>· ${this.esc(this.shortWho(j.created_by))}</span>` : ''}</div></div>`;
  }

  private problemHtml(j: OutboxJob, L: LeadSummary): string {
    const url = this.safeUrl(L.thread_url);
    const label = j.status_label || (j.status === 'uncertain' ? 'Check the platform — may have sent' : 'Not sent');
    // "uncertain" may already be on the platform: no Copy there, to avoid a double reply.
    const copy = j.status === 'uncertain' ? '' : this.copyBtn(j);
    return `<div class="lx-b out problem"><div class="lx-b-text">${this.esc(j.text)}</div><div class="lx-b-warn">⚠ ${this.esc(label)}${j.message ? ` — ${this.esc(j.message)}` : ''}</div><div class="lx-b-meta">${copy}${j.created_by ? `<span>${copy ? '· ' : ''}${this.esc(this.shortWho(j.created_by))}</span>` : ''}${j.updated_at || j.created_at ? `<span>· ${this.esc(this.fmtT(j.updated_at || j.created_at))}</span>` : ''}${url ? `<a class="lx-link" href="${this.esc(url)}" target="_blank" rel="noopener noreferrer">Check on ${this.esc(this.platShort(L.platform))} ↗</a>` : ''}</div></div>`;
  }

  private hintHtml(): string {
    const L = this.curLead();
    const d = this.data;
    if (!L || !d) return '';
    const p = d.platforms?.[L.platform];
    const prose = this.esc(this.platProse(L.platform));
    const block = this.sendBlock(L);
    if (block) return `<div class="lx-hint">${this.esc(block)} Replies can't be sent until it's back on. <button type="button" class="lx-link" data-lx-act="agent">Open the Leads agent</button></div>`;
    if (d.agent && d.agent.online === false) return `<div class="lx-hint">The Leads agent is offline — a reply you send now waits in the queue until it reconnects.</div>`;
    if (p && NEEDS_PERSON.includes(String(p.state))) return `<div class="lx-hint">${prose} needs someone to sign in — replies wait until then. <button type="button" class="lx-link" data-lx-act="agent">Open the Leads agent</button></div>`;
    return '';
  }

  private composerHtml(): string {
    const L = this.curLead();
    if (!L) return '';
    const draft = this.draftOf(L.id);
    const empty = !draft.trim();
    const first = String(L.name || '').trim().split(/\s+/)[0] || 'the renter';
    const checking = this.verifying === L.id;
    // AI-unblock: a pending AI draft never locks the box, Send or Add note.
    const lock = this.sending || checking;
    const drafting = this.drafting.has(L.id);
    const block = this.sendBlock(L);
    const st = String(L.status || 'open');
    const note = this.cnotes.get(L.id) || null;
    const aiBtn = drafting
      ? `<button type="button" class="lx-btn sm" data-lx-act="cancel-draft" title="Stop waiting for the AI — you can keep typing either way">✕ Cancel draft</button>`
      : `<button type="button" class="lx-btn sm" data-lx-act="ai-draft" ${this.sending ? 'disabled' : ''} title="Claude writes a draft into the box below — nothing is sent">✨ Draft with AI</button>`;
    return `
      <div class="lx-compose">
        <div class="lx-ai">
          ${aiBtn}
          <input class="lx-in" data-lx-key="ai" maxlength="500" placeholder="Optional guidance for the AI, e.g. “offer a tour Saturday afternoon”" value="${this.esc(this.aiInstr)}">
        </div>
        <textarea class="lx-ta" data-lx-key="draft" rows="3" maxlength="4000" ${lock ? 'readonly' : ''} placeholder="${this.esc(`Reply to ${first} on ${this.platProse(L.platform)}…  (⌘/Ctrl + Enter to send)`)}">${this.esc(draft)}</textarea>
        <div class="lx-crow">
          <select class="lx-in lx-status-sel" data-lx-key="status" ${this.statusBusy ? 'disabled' : ''} title="Lead status">
            ${Object.keys(STATUS_LABEL).map(s => `<option value="${s}" ${s === st ? 'selected' : ''}>${STATUS_LABEL[s]}</option>`).join('')}
          </select>
          ${st === 'open' ? `<button type="button" class="lx-btn sm" data-lx-act="handled" ${this.statusBusy ? 'disabled' : ''} title="Nothing more to do right now — stops the response clock">✓ Mark handled</button>` : ''}
          <button type="button" class="lx-btn sm" data-lx-act="note" ${empty || this.noting || lock ? 'disabled' : ''} title="Save the text above as an internal note — only the team sees it">${this.noting ? 'Saving…' : 'Add internal note'}</button>
          <span class="lx-cnote ${note ? note.tone : ''}" role="status">${note ? this.esc(note.text) : ''}</span>
          <button type="button" class="lx-btn primary" data-lx-act="send" ${empty || lock || block ? 'disabled' : ''} title="${this.esc(block)}">${this.sending ? 'Queuing…' : checking ? 'Checking…' : `Send via ${this.esc(this.platShort(L.platform))}`}</button>
        </div>
      </div>`;
  }

  // ── rendering: profile ────────────────────────────────────────────────────
  private profileHtml(): string {
    const L = this.curLead();
    if (!L) return '';
    const D = this.detailFor(L.id)?.lead || null;
    const props = this.data?.properties || [];
    const pn = this.pnote && this.pnote.lead === L.id ? this.pnote : null;

    const field = (key: 'name' | 'email' | 'phone', label: string, val: any) => {
      if (this.profileEdit && this.profileEdit.field === key) {
        return `<div class="lx-f edit"><div class="lx-f-l">${label}</div><input class="lx-in" data-lx-key="edit-${key}" type="${key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'text'}" value="${this.esc(this.profileEdit.value)}" maxlength="200"><button type="button" class="lx-btn sm" data-lx-act="edit-save" ${this.profileBusy ? 'disabled' : ''}>Save</button><button type="button" class="lx-icon" data-lx-act="edit-cancel" title="Cancel">✕</button></div>`;
      }
      return `<div class="lx-f"><div class="lx-f-l">${label}</div><div class="lx-f-v">${val ? this.esc(val) : '<span class="lx-faint">—</span>'}</div><button type="button" class="lx-icon" data-lx-act="edit" data-lx-field="${key}" title="Edit ${label.toLowerCase()}" aria-label="Edit ${label.toLowerCase()}">✎</button></div>`;
    };

    // property
    const inList = !L.listing_id || props.some(p => String(p.listing_id) === String(L.listing_id));
    const opts = [`<option value="">— not matched —</option>`]
      .concat(!inList ? [`<option value="${this.esc(L.listing_id)}" selected>${this.esc(L.listing_name || L.listing_id)}</option>`] : [])
      .concat(props.map(p => `<option value="${this.esc(p.listing_id)}" ${String(p.listing_id) === String(L.listing_id || '') ? 'selected' : ''}>${this.esc(p.name)}</option>`));
    const cands = (D?.match_candidates || []).filter(c => c && c.listing_id && String(c.listing_id) !== String(L.listing_id || '')).slice(0, 4);
    const scoreTxt = (s: any) => { const x = Number(s); if (!Number.isFinite(x) || x <= 0) return ''; return ` · ${Math.round(x <= 1 ? x * 100 : x)}%`; };

    // assignee
    const people = this.people || [];
    const me = this.myEmail();
    const aVal = this.assigneeDraft != null ? this.assigneeDraft : (L.assignee || '');
    const aName = this.personName(L.assignee);

    // facts
    const prof = (D && D.profile) || {};
    const factRows = FACTS.map(([k, label]) => {
      const v = prof[k];
      if (v == null || v === '') return '';
      let txt = String(v);
      if (k === 'move_in' || k === 'move_out') txt = Formatters.formatDate(String(v)) === '--' ? String(v) : Formatters.formatDate(String(v));
      else if (k === 'lease_months') txt = `${v} month${Number(v) === 1 ? '' : 's'}`;
      return `<tr><th>${label}</th><td>${this.esc(txt)}</td></tr>`;
    }).join('');

    const tags = Array.isArray(L.tags) ? L.tags.filter(Boolean) : [];
    const firstResp = L.first_response_at
      ? `${this.esc(this.fmtT(L.first_response_at))}${L.first_in_at && this.ts(L.first_response_at) >= this.ts(L.first_in_at) ? ` <span class="lx-faint">(after ${this.esc(this.durMin((this.ts(L.first_response_at) - this.ts(L.first_in_at)) / 60000))})</span>` : ''}`
      : '<span class="lx-faint">not yet</span>';

    const dupId = L.duplicate_of;
    const dup = dupId ? (this.data?.leads || []).find(x => x.id === dupId) : null;

    const hist = (D?.history || []).slice().reverse();

    return `
      <div class="lx-prof-head"><span>Lead details</span><button type="button" class="lx-icon" data-lx-act="toggle-profile" title="Close details" aria-label="Close details">✕</button></div>
      ${pn ? `<div class="lx-pnote ${pn.tone}" role="status">${this.esc(pn.text)}</div>` : ''}
      <div class="lx-sec">
        <div class="lx-sec-h">Renter</div>
        ${field('name', 'Name', L.name)}
        ${field('email', 'Email', L.email)}
        ${field('phone', 'Phone', L.phone)}
      </div>
      <div class="lx-sec">
        <div class="lx-sec-h">Property</div>
        ${L.property_text ? `<div class="lx-meta" style="margin-bottom:6px;">${this.esc(this.platShort(L.platform))} calls it “${this.esc(L.property_text)}”</div>` : ''}
        <select class="lx-in lx-wide" data-lx-key="listing" ${this.profileBusy ? 'disabled' : ''} aria-label="Matched property">${opts.join('')}</select>
        ${L.listing_id && L.match_via ? `<div class="lx-meta" style="margin-top:5px;">${this.esc(MATCH_LABEL[L.match_via] || L.match_via)}</div>` : ''}
        ${cands.length ? `<div class="lx-meta" style="margin-top:8px;">${L.listing_id ? 'Other likely matches' : 'Likely matches'}</div><div class="lx-tags" style="margin-top:4px;">${cands.map(c => `<button type="button" class="lx-chip" data-lx-act="match" data-lx-listing="${this.esc(c.listing_id)}" title="Match this lead to ${this.esc(c.name)}">${this.esc(c.name)}${this.esc(scoreTxt(c.score))}</button>`).join('')}</div>` : ''}
      </div>
      <div class="lx-sec">
        <div class="lx-sec-h">Assignee</div>
        <input class="lx-in lx-wide" data-lx-key="assignee" list="lx-people" placeholder="Unassigned — type a name or email" value="${this.esc(aVal)}" ${this.profileBusy ? 'disabled' : ''} autocomplete="off" aria-label="Assignee">
        <datalist id="lx-people">${people.map(p => `<option value="${this.esc(p.email)}">${this.esc(p.full_name)}</option>`).join('')}</datalist>
        <div class="lx-meta" style="margin-top:5px; display:flex; gap:10px; align-items:center;">
          ${aName ? `<span>${this.esc(aName)}</span>` : ''}
          ${me && String(L.assignee || '').toLowerCase() !== me ? `<button type="button" class="lx-link" data-lx-act="assign-me">Assign to me</button>` : ''}
          ${L.assignee ? `<button type="button" class="lx-link" data-lx-act="unassign">Unassign</button>` : ''}
        </div>
      </div>
      <div class="lx-sec">
        <div class="lx-sec-h">What they told us</div>
        ${D ? (factRows ? `<table class="lx-facts">${factRows}</table>` : `<div class="lx-meta">Nothing captured yet — details like move-in date, budget and pets fill in as the conversation goes.</div>`) : `<div class="lx-meta">Loading…</div>`}
        ${(L.intent && INTENT_LABEL[L.intent]) || tags.length ? `<div class="lx-tags" style="margin-top:8px;">${L.intent ? `<span class="lx-tag strong">${this.esc(INTENT_LABEL[L.intent] || L.intent)}</span>` : ''}${tags.map(t => `<span class="lx-tag">${this.esc(t)}</span>`).join('')}</div>` : ''}
      </div>
      <div class="lx-sec">
        <div class="lx-sec-h">Timeline</div>
        <table class="lx-facts">
          <tr><th>Created</th><td>${this.esc(this.fmtT(L.created_at)) || '—'}</td></tr>
          <tr><th>First message</th><td>${this.esc(this.fmtT(L.first_in_at)) || '—'}</td></tr>
          <tr><th>First reply</th><td>${firstResp}</td></tr>
          <tr><th>Last activity</th><td>${this.esc(this.fmtT(L.last_message_at)) || '—'}</td></tr>
        </table>
        ${dupId ? `<div class="lx-dup">⚑ Possible duplicate — same email or phone as <button type="button" class="lx-link" data-lx-act="open" data-lx-id="${this.esc(dupId)}">${dup ? `${this.esc(dup.name || 'another lead')} (${this.esc(this.platShort(dup.platform))})` : 'another lead'}</button> in the last 30 days.</div>` : ''}
      </div>
      <div class="lx-sec">
        <div class="lx-sec-h">MTR pipeline</div>
        ${L.mtr_lead_id
          ? `<div class="lx-ok">In pipeline ✓${this.opts.openPipelineTab ? ` <button type="button" class="lx-link" data-lx-act="pipeline-view" data-lx-mtr="${this.esc(L.mtr_lead_id)}" title="Open this renter's card on the Pipeline board">View pipeline →</button>` : ''}</div>`
          : `<button type="button" class="lx-btn sm" data-lx-act="pipeline" ${this.pipelineBusy ? 'disabled' : ''} title="Create a card on the MTR Pipeline board for this renter">${this.pipelineBusy ? 'Adding…' : '＋ Add to pipeline'}</button>`}
      </div>
      <div class="lx-sec">
        <details class="lx-hist" data-lx-hist ${this.historyOpen ? 'open' : ''}>
          <summary>History${hist.length ? ` (${hist.length})` : ''}</summary>
          ${hist.length ? `<ul>${hist.map(h => `<li><span>${this.esc(h.what)}</span><span class="lx-faint">${this.esc(this.shortWho(h.by))}${h.at ? ` · ${this.esc(this.fmtT(h.at))}` : ''}</span></li>`).join('')}</ul>` : `<div class="lx-meta" style="margin-top:6px;">${D ? 'No changes recorded yet.' : 'Loading…'}</div>`}
        </details>
      </div>`;
  }

  // ── events (delegated on the root — survives every partial repaint) ───────
  private bindRoot(root: HTMLElement): void {
    root.addEventListener('click', (e) => this.onClick(e));
    root.addEventListener('input', (e) => this.onInput(e));
    root.addEventListener('change', (e) => this.onChange(e));
    root.addEventListener('keydown', (e) => this.onKeydown(e as KeyboardEvent));
    // toggle + scroll don't bubble — capture them on the way down
    root.addEventListener('toggle', (e) => { const t = e.target as HTMLDetailsElement; if (t && t.dataset && 'lxHist' in t.dataset) this.historyOpen = t.open; }, true);
    root.addEventListener('scroll', (e) => { const t = e.target as HTMLElement; if (t && t.classList && t.classList.contains('lx-rows')) this.listScroll = t.scrollTop; }, true);
  }

  private run(p: Promise<unknown>): void { p.catch(err => console.error('[leads]', err)); }

  private onClick(e: Event): void {
    const el = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-lx-act]');
    if (!el || !this.rootEl || !this.rootEl.contains(el)) return;
    if ((el as HTMLButtonElement).disabled) return;
    const d = el.dataset;
    switch (d.lxAct) {
      case 'open': if (d.lxId) this.run(this.select(d.lxId)); break;
      case 'back': if (this.selectedId) this.cnotes.delete(this.selectedId); this.selectedId = null; this.profileEdit = null; this.assigneeDraft = null; this.pnote = null; this.draw(); break;
      case 'view': if (d.lxView && d.lxView !== this.view) this.run(this.setView(d.lxView as View)); break;
      case 'plat': this.platFilter = (d.lxPlat || '') as any; this.patchList(); break;
      case 'toggle-waiting': this.waitingOnly = !this.waitingOnly; this.patchList(); break;
      case 'toggle-mine': this.mineOnly = !this.mineOnly; this.patchList(); break;
      case 'clear-filters': this.platFilter = ''; this.waitingOnly = false; this.mineOnly = false; this.patchList(); break;
      case 'agent': if (this.opts.openAgentTab) this.opts.openAgentTab(); break;
      case 'pipeline-view': if (this.opts.openPipelineTab) this.opts.openPipelineTab(d.lxMtr || undefined); break;
      case 'refresh': this.run(this.refreshAll()); break;
      case 'retry-list':
        this.listError = '';
        if (!this.data) { this.draw(); this.run(this.loadList('full')); }
        else { this.listLoading = true; this.patchList(); this.run(this.loadList('quiet')); }
        break;
      case 'retry-detail': if (this.selectedId) { this.detailError = ''; this.patchThread(); this.run(this.loadDetail(this.selectedId, false)); } break;
      case 'toggle-profile': {
        this.profileOpen = !this.profileOpen;
        this.rootEl.querySelector('[data-lx-wrap]')?.classList.toggle('prof-open', this.profileOpen);
        break;
      }
      case 'ai-draft': this.run(this.aiDraft()); break;
      case 'send': this.run(this.send()); break;
      case 'note': this.run(this.addNote()); break;
      case 'handled': this.run(this.setStatus('handled')); break;
      case 'cancel-reply': if (d.lxJob) this.run(this.cancelReply(d.lxJob)); break;
      case 'copy-reply': if (d.lxJob) this.run(this.copyReply(d.lxJob, el)); break;
      case 'copy-cancel-reply': if (d.lxJob) this.run(this.copyWaitingReply(d.lxJob, el)); break;
      case 'cancel-draft': if (this.selectedId) this.cancelDraft(this.selectedId); break;
      case 'edit': {
        const f = d.lxField as 'name' | 'email' | 'phone';
        const L = this.curLead();
        if (!L || !f) break;
        this.profileEdit = { field: f, value: String((L as any)[f] || '') };
        this.pnote = null;
        this.patchProfile();
        const inp = this.rootEl.querySelector<HTMLInputElement>(`[data-lx-key="edit-${f}"]`);
        if (inp) { inp.focus(); inp.select(); }
        break;
      }
      case 'edit-save': this.run(this.saveEdit()); break;
      case 'edit-cancel': this.profileEdit = null; this.pnote = null; this.patchProfile(); break;
      case 'match': if (d.lxListing) this.run(this.updateLead({ listing_id: d.lxListing }, 'profile', 'Property matched.')); break;
      case 'assign-me': { const me = this.myEmail(); if (me) this.run(this.updateLead({ assignee: me }, 'profile', 'Assigned to you.')); break; }
      case 'unassign': this.assigneeDraft = null; this.run(this.updateLead({ assignee: null }, 'profile', 'Unassigned.')); break;
      case 'pipeline': this.run(this.addToPipeline()); break;
    }
  }

  private onInput(e: Event): void {
    const t = e.target as HTMLInputElement;
    const key = t && t.dataset ? t.dataset.lxKey : '';
    if (!key) return;
    if (key === 'draft') {
      if (!this.selectedId) return;
      this.drafts.set(this.selectedId, t.value);   // mirror so redraws/polls never lose it
      this.touchDraft(this.selectedId);             // AI-unblock: they're writing it themselves
      if (!t.value.trim()) this.draftKeys.delete(this.selectedId);   // cleared → the next draft gets a fresh key
      this.updateComposerButtons();
    } else if (key === 'ai') this.aiInstr = t.value;
    else if (key.startsWith('edit-') && this.profileEdit) this.profileEdit.value = t.value;
    else if (key === 'assignee') this.assigneeDraft = t.value;
  }

  private onChange(e: Event): void {
    const t = e.target as HTMLInputElement | HTMLSelectElement;
    const key = t && t.dataset ? t.dataset.lxKey : '';
    if (key === 'status') this.run(this.setStatus(t.value));
    else if (key === 'listing') this.run(this.updateLead({ listing_id: t.value || null }, 'profile', t.value ? 'Property matched.' : 'Property unmatched.'));
    else if (key === 'assignee') this.run(this.commitAssignee(t.value));
  }

  private onKeydown(e: KeyboardEvent): void {
    const t = e.target as HTMLElement;
    const key = t && t.dataset ? t.dataset.lxKey : '';
    if (!key) return;
    if (key === 'draft' && e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); this.run(this.send()); }
    else if (key === 'ai' && e.key === 'Enter') { e.preventDefault(); this.run(this.aiDraft()); }
    else if (key.startsWith('edit-')) {
      if (e.key === 'Enter') { e.preventDefault(); this.run(this.saveEdit()); }
      else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); this.profileEdit = null; this.pnote = null; this.patchProfile(); }
    } else if (key === 'assignee' && e.key === 'Enter') { e.preventDefault(); (t as HTMLInputElement).blur(); }
  }

  // ── actions ───────────────────────────────────────────────────────────────
  private async select(id: string): Promise<void> {
    if (id === this.selectedId && this.detailFor(id)) { this.pendingScroll = true; this.patchThread(); return; }
    if (this.selectedId) this.cnotes.delete(this.selectedId);   // notes are per lead; the one we leave is done
    this.selectedId = id;
    this.detailError = '';
    this.pnote = null;
    this.profileEdit = null;
    this.assigneeDraft = null;
    this.pendingScroll = true;
    this.draw();
    await this.loadDetail(id, false);
  }

  private async setView(v: View): Promise<void> {
    this.view = v;
    this.listLoading = true;
    this.listScroll = 0;
    this.patchList();
    await this.loadList('quiet');
  }

  private async refreshAll(): Promise<void> {
    await this.loadList('quiet');
    if (this.selectedId) await this.loadDetail(this.selectedId, true);
  }

  // Composer/profile notes are keyed by lead; the finally-repaints below always
  // paint whichever lead is open NOW, so a mid-request switch never leaves the
  // new lead's composer showing a stale lock.
  private note(id: string, n: Note | null): void { if (n) this.cnotes.set(id, n); else this.cnotes.delete(id); }
  private pNote(id: string, n: Note | null): void { this.pnote = n ? { ...n, lead: id } : null; }

  // Why Send is off for this lead ('' = it isn't). The server reports every
  // platform enabled:false while the agent's master switch is off.
  private sendBlock(L: LeadSummary | null): string {
    const d = this.data;
    if (!d || !L) return '';
    const p = d.platforms?.[L.platform];
    if (!p || (p.enabled !== false && p.state !== 'disabled')) return '';
    const allOff = PLATFORMS.every(pid => { const q = d.platforms?.[pid]; return !q || q.enabled === false || q.state === 'disabled'; });
    return allOff ? 'Sending is off — the Leads agent is switched off in its settings.' : `${this.platProse(L.platform)} is turned off in the agent settings.`;
  }

  // Keep Send / Add-note enabled state in step without re-rendering the composer.
  private updateComposerButtons(): void {
    const L = this.curLead();
    const scope = this.rootEl?.querySelector('[data-lx-region="composer"]');
    if (!L || !scope) return;
    const empty = !this.draftOf(L.id).trim();
    const lock = this.sending || this.verifying === L.id;   // AI-unblock: drafts never lock
    const block = this.sendBlock(L);
    const send = scope.querySelector<HTMLButtonElement>('[data-lx-act="send"]');
    const note = scope.querySelector<HTMLButtonElement>('[data-lx-act="note"]');
    if (send) { send.disabled = empty || lock || !!block; send.title = block; }
    if (note) note.disabled = empty || this.noting || lock;
  }

  private newKey(): string {
    try { if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID(); } catch { /* insecure context */ }
    return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
  }
  private keyFor(id: string): string {
    let k = this.draftKeys.get(id);
    if (!k) { k = this.newKey(); this.draftKeys.set(id, k); }
    return k;
  }
  private clearDraft(id: string): void { this.drafts.delete(id); this.draftKeys.delete(id); }

  // Idempotent send: one client_key per draft, so a retry after an ambiguous
  // failure (network drop / 5xx) can never queue the same reply twice.
  private async send(): Promise<void> {
    const id = this.selectedId;
    const L = this.curLead();
    if (!id || !L || this.sending || this.verifying) return;
    const text = this.draftOf(id).trim();
    if (!text) return;
    const block = this.sendBlock(L);
    if (block) { this.note(id, { text: block, tone: 'err' }); this.patchComposer(); return; }
    if (text.length > 4000) { this.note(id, { text: 'That reply is over 4,000 characters — shorten it first.', tone: 'err' }); this.patchComposer(); return; }
    const prose = this.platProse(L.platform);
    if (!confirm(`Send this reply through ${prose} as the West Coast Homestays account?`)) return;
    this.touchDraft(id);   // AI-unblock: an AI draft that lands after this is dropped
    const key = this.keyFor(id);
    this.sending = true;
    this.note(id, null);
    this.patchComposer();
    let r: ReqResult | null = null;
    try {
      r = await this.req('POST', `${API}/${encodeURIComponent(id)}/reply`, { text, client_key: key });
    } finally {
      this.sending = false;
      this.patchComposer();
    }
    if (r.ok) { await this.afterQueued(id, r.data && r.data.job ? r.data.job as OutboxJob : null, prose, !!(r.data && r.data.duplicate)); return; }
    if (r.status !== 0 && r.status < 500) {
      this.note(id, { text: `Not queued — ${r.error}`, tone: 'err' });
      this.patchComposer();
      return;
    }
    // Ambiguous: the request may have landed before the connection dropped.
    // Look before saying anything; Send stays disabled until we know.
    this.verifying = id;
    this.note(id, { text: 'Checking whether it was queued…', tone: 'info' });
    this.patchComposer();
    let fresh: { lead: LeadDetail; replies: OutboxJob[] } | null = null;
    try {
      fresh = await this.loadDetail(id, true, true);   // bypasses the typing guard on purpose
    } finally {
      this.verifying = null;
    }
    const hit = fresh ? fresh.replies.find(j => j && j.status !== 'cancelled' && String(j.text || '').trim() === text
      && (!j.created_at || Date.now() - this.ts(j.created_at) < 30 * 60000)) : null;
    if (hit) { await this.afterQueued(id, hit, prose, false); return; }
    this.note(id, fresh
      ? { text: `Not queued — ${r.error} Press Send to try again; it won't double-send.`, tone: 'err' }
      : { text: `Couldn't confirm whether it was queued — ${r.error} Check the thread; pressing Send again won't double-send.`, tone: 'err' });
    this.patchComposer();
  }

  private async afterQueued(id: string, job: OutboxJob | null, prose: string, duplicate: boolean): Promise<void> {
    this.clearDraft(id);   // success → the next draft gets a fresh key
    const cur = this.detailFor(id);
    if (cur && job && !cur.replies.some(x => x.id === job.id)) cur.replies.unshift(job);   // optimistic pending bubble
    const hold = job && job.status === 'queued' ? Math.ceil((this.ts(job.send_at) - Date.now()) / 1000) : 0;
    this.note(id, duplicate
      ? { text: 'Already queued — that reply was received earlier, so it wasn\'t added twice.', tone: 'ok' }
      : hold > 0
        ? { text: `Queued — sends through ${prose} in ${hold}s. Cancel on the bubble to undo.`, tone: 'ok' }
        : { text: `Queued — the agent will type it into ${prose} shortly.`, tone: 'ok' });
    if (this.selectedId === id) { this.pendingScroll = true; this.patchThread(); }
    this.patchComposer();
    this.patchRows();
    await this.loadDetail(id, true, true);
    void this.loadList('quiet');
  }

  // AI-unblock: the AI draft is optional help running in the background. It is
  // per lead, gives up after a minute (or on Cancel draft), and only fills the
  // box if the person hasn't typed, sent or saved a note in the meantime.
  private async aiDraft(): Promise<void> {
    const id = this.selectedId;
    if (!id || this.drafting.has(id) || this.sending || this.verifying === id) return;
    if (this.draftOf(id).trim() && !confirm('Replace your current draft with an AI draft?')) return;
    const job = { ctl: new AbortController(), base: this.draftOf(id), touched: false, why: '' as '' | 'cancel' | 'timeout' };
    this.drafting.set(id, job);
    const timer = setTimeout(() => { job.why = 'timeout'; job.ctl.abort(); }, 60000);
    const pendingNote: Note = { text: 'Drafting a reply… you can keep typing; the draft only fills an untouched box.', tone: 'info' };
    this.note(id, pendingNote);
    this.patchComposer();
    // Report the outcome only over our own "Drafting…" note, never over a
    // newer one (e.g. "Queued — sends in 20s" after they sent by themselves).
    const say = (n: Note) => { if (this.cnotes.get(id) === pendingNote) this.note(id, n); };
    const instr = this.aiInstr.trim();
    let r: ReqResult | null = null;
    let filled = false;
    try {
      r = await this.req('POST', `${API}/${encodeURIComponent(id)}/draft`, instr ? { instructions: instr } : {}, job.ctl.signal);
      if (job.why === 'cancel') {
        say({ text: 'AI draft cancelled.', tone: 'info' });
      } else if (job.why === 'timeout') {
        say({ text: 'The AI took too long — write the reply by hand, or try again.', tone: 'err' });
      } else if (!r.ok || !r.data || typeof r.data.text !== 'string') {
        say({ text: `Couldn't draft — ${r.ok ? 'the server sent no text.' : r.error}`, tone: 'err' });
      } else if (job.touched || this.draftOf(id) !== job.base) {
        // they typed / sent / saved a note meanwhile: their text wins
        say({ text: 'AI draft arrived after you started writing — kept your text.', tone: 'info' });
      } else {
        this.drafts.set(id, r.data.text);
        this.draftKeys.delete(id);   // replaced → fresh key
        filled = true;
        this.note(id, { text: 'AI draft ready — read it and edit before sending. Nothing has been sent.', tone: 'info' });
      }
    } finally {
      clearTimeout(timer);
      if (this.drafting.get(id) === job) this.drafting.delete(id);
      this.patchComposer();
      this.patchRows();
    }
    if (filled && this.selectedId === id) {
      const ta = this.rootEl?.querySelector<HTMLTextAreaElement>('[data-lx-key="draft"]');
      if (ta) { ta.focus(); const n = ta.value.length; try { ta.setSelectionRange(n, n); } catch { /* ignore */ } ta.scrollTop = ta.scrollHeight; }
    }
  }
  private touchDraft(id: string | null): void {
    const job = id ? this.drafting.get(id) : null;
    if (job) job.touched = true;
  }
  private cancelDraft(id: string): void {
    const job = this.drafting.get(id);
    if (!job) return;
    job.why = 'cancel';
    job.ctl.abort();   // the server stops the Claude call when the request closes
  }

  // AI-unblock: copy a waiting / failed reply to send it by hand.
  private async copyReply(jobId: string, btn: HTMLElement): Promise<void> {
    const d = this.detailFor(this.selectedId);
    const j = d ? d.replies.find(x => x && x.id === jobId) : null;
    const text = String((j && j.text) || '');
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      btn.textContent = '✓ Copied';
      setTimeout(() => { if (btn.isConnected) btn.textContent = 'Copy reply'; }, 1500);
    } catch {
      window.prompt('Copy this reply:', text);
    }
  }

  // AI-unblock: Copy reply on a reply that is waiting out an AI outage. The
  // automatic send is cancelled FIRST and the text is copied only once that
  // worked, so a reply sent by hand can never also go out from the agent
  // (the server refuses the cancel once the reply may be in the platform's box).
  private async copyWaitingReply(jobId: string, btn: HTMLElement): Promise<void> {
    const id = this.selectedId;
    if (!id) return;
    const before = this.detailFor(id)?.replies.find(x => x && x.id === jobId) || null;
    (btn as HTMLButtonElement).disabled = true;
    const r = await this.req('POST', `${API}/replies/${encodeURIComponent(jobId)}/cancel`, {});
    if (btn.isConnected) (btn as HTMLButtonElement).disabled = false;
    if (!r.ok) {
      this.note(id, { text: `Not copied — the automatic send couldn't be stopped (${r.error}). Don't send it by hand until you've checked the conversation on the platform.`, tone: 'err' });
      this.patchComposer();
      await this.loadDetail(id, true, true);
      return;
    }
    const job = r.data && r.data.job ? r.data.job as OutboxJob : null;
    const text = String((job && job.text) || (before && before.text) || '');
    const cur = this.detailFor(id);
    if (cur) {
      const i = cur.replies.findIndex(x => x.id === jobId);
      if (i >= 0) cur.replies[i] = job || { ...cur.replies[i], status: 'cancelled' };
    }
    let copied = true;
    try { await navigator.clipboard.writeText(text); }
    catch { copied = window.prompt('Automatic send cancelled — copy this reply:', text) !== null; }
    const prose = this.platProse((cur && cur.lead.platform) || (this.curLead()?.platform) || '');
    // Not copied (no clipboard, prompt dismissed): keep the text where it can
    // still be copied from, since a cancelled reply leaves the thread.
    const restored = !copied && !!text && !this.draftOf(id).trim();
    if (restored) { this.drafts.set(id, text); this.draftKeys.delete(id); }
    this.note(id, {
      text: copied
        ? `Automatic send cancelled and the reply copied — paste it into ${prose} and send it there.`
        : `Automatic send cancelled — the agent won't send this reply.${restored ? ` Its text is back in the box — copy it from there and send it by hand on ${prose}.` : ` Send it by hand on ${prose}.`}`,
      tone: 'info',
    });
    if (this.selectedId === id) this.patchThread();
    this.patchComposer();
    this.patchRows();
    await this.loadDetail(id, true, true);
    void this.loadList('quiet');
  }

  private async addNote(): Promise<void> {
    const id = this.selectedId;
    if (!id || this.noting) return;
    const text = this.draftOf(id).trim();
    if (!text) return;
    this.touchDraft(id);
    this.noting = true;
    this.note(id, null);
    this.patchComposer();
    let ok = false;
    try {
      const r = await this.req('POST', `${API}/${encodeURIComponent(id)}`, { note: text });
      ok = r.ok;
      if (!r.ok) this.note(id, { text: `Note not saved — ${r.error}`, tone: 'err' });
      else {
        this.clearDraft(id);
        this.applyLead(r.data && r.data.lead);
        this.note(id, { text: 'Internal note added.', tone: 'ok' });
      }
    } finally {
      this.noting = false;
      this.patchComposer();
      this.patchRows();
    }
    if (ok && this.selectedId === id) { this.pendingScroll = true; this.patchThread(); }
  }

  private async setStatus(status: string): Promise<void> {
    const id = this.selectedId;
    if (!id || this.statusBusy || !STATUS_LABEL[status]) return;
    this.statusBusy = true;
    this.patchComposer();
    let ok = false;
    try {
      const r = await this.req('POST', `${API}/${encodeURIComponent(id)}`, { status });
      ok = r.ok;
      if (!r.ok) this.note(id, { text: `Status not changed — ${r.error}`, tone: 'err' });
      else {
        this.applyLead(r.data && r.data.lead);
        this.note(id, { text: status === 'open' ? 'Reopened.' : `Marked ${STATUS_LABEL[status].toLowerCase()}.`, tone: 'ok' });
      }
    } finally {
      this.statusBusy = false;
      this.patchComposer();   // also reverts the <select> after a failure
      this.patchRows();
    }
    if (ok) {
      if (this.selectedId === id) this.patch('head', this.headHtml());
      void this.loadList('quiet');
    }
  }

  private async cancelReply(jobId: string): Promise<void> {
    const id = this.selectedId;
    if (!id) return;
    const r = await this.req('POST', `${API}/replies/${encodeURIComponent(jobId)}/cancel`, {});
    if (!r.ok) {
      this.note(id, { text: `Couldn't cancel — ${r.error}`, tone: 'err' });
      this.patchComposer();
      await this.loadDetail(id, true, true);
      return;
    }
    const cur = this.detailFor(id);
    const job = r.data && r.data.job ? r.data.job as OutboxJob : null;
    if (cur) {
      const i = cur.replies.findIndex(x => x.id === jobId);
      if (i >= 0) cur.replies[i] = job || { ...cur.replies[i], status: 'cancelled' };
      // give the text back so it can be edited and re-sent (as a NEW draft/key —
      // reusing the cancelled job's key would just return that cancelled job)
      const text = (job && job.text) || (i >= 0 ? cur.replies[i].text : '') || '';
      if (text && !this.draftOf(id).trim()) { this.drafts.set(id, text); this.draftKeys.delete(id); }
    }
    this.note(id, { text: 'Reply cancelled — its text is back in the box if you want to edit it.', tone: 'info' });
    if (this.selectedId === id) this.patchThread();
    this.patchComposer();
    this.patchRows();
    await this.loadDetail(id, true, true);
    void this.loadList('quiet');
  }

  // POST /:id for profile fields; errors land in the profile pane.
  private async updateLead(body: Record<string, any>, where: 'profile' | 'composer', okText: string): Promise<boolean> {
    const id = this.selectedId;
    if (!id || this.profileBusy) return false;
    this.profileBusy = true;
    this.pNote(id, null);
    this.patchProfile();
    let ok = false;
    try {
      const r = await this.req('POST', `${API}/${encodeURIComponent(id)}`, body);
      ok = r.ok;
      if (!r.ok) {
        const n: Note = { text: `Not saved — ${r.error}`, tone: 'err' };
        if (where === 'profile') this.pNote(id, n); else this.note(id, n);
      } else {
        this.applyLead(r.data && r.data.lead);
        this.pNote(id, { text: okText, tone: 'ok' });
      }
    } finally {
      this.profileBusy = false;
      this.patchProfile();
      this.patchComposer();
    }
    if (ok) {
      if (this.selectedId === id) this.patch('head', this.headHtml());
      this.patchRows();
      void this.loadList('quiet');
    }
    return ok;
  }

  private async saveEdit(): Promise<void> {
    const pe = this.profileEdit;
    const id = this.selectedId;
    if (!pe || !id || this.profileBusy) return;
    const v = pe.value.trim();
    if (pe.field === 'name' && !v) { this.pNote(id, { text: "Name can't be empty.", tone: 'err' }); this.patchProfile(); return; }
    if (pe.field === 'email' && v && !EMAIL_RE.test(v)) { this.pNote(id, { text: "That doesn't look like an email address.", tone: 'err' }); this.patchProfile(); return; }
    const label = pe.field === 'name' ? 'Name' : pe.field === 'email' ? 'Email' : 'Phone';
    this.profileEdit = null;
    const ok = await this.updateLead({ [pe.field]: v || null }, 'profile', `${label} saved.`);
    if (!ok && !this.profileEdit && this.selectedId === id) { this.profileEdit = pe; this.patchProfile(); }   // keep what they typed
  }

  private async commitAssignee(raw: string): Promise<void> {
    const L = this.curLead();
    if (!L) return;
    const v = raw.trim();
    let email: string | null = null;
    if (v) {
      const hit = (this.people || []).find(p => p.email.toLowerCase() === v.toLowerCase() || p.full_name.toLowerCase() === v.toLowerCase());
      email = (hit ? hit.email : v).toLowerCase();
      if (!EMAIL_RE.test(email)) { this.pNote(L.id, { text: 'Pick a teammate from the list or type their email.', tone: 'err' }); this.patchProfile(); return; }
    }
    if ((email || null) === (String(L.assignee || '').toLowerCase() || null)) { this.assigneeDraft = null; return; }
    const ok = await this.updateLead({ assignee: email }, 'profile', email ? `Assigned to ${this.personName(email) || email}.` : 'Unassigned.');
    if (ok) { this.assigneeDraft = null; this.patchProfile(); }
  }

  private async addToPipeline(): Promise<void> {
    const id = this.selectedId;
    if (!id || this.pipelineBusy) return;
    this.pipelineBusy = true;
    this.pNote(id, null);
    this.patchProfile();
    try {
      const r = await this.req('POST', `${API}/${encodeURIComponent(id)}/pipeline`, {});
      if (!r.ok || !r.data) {
        this.pNote(id, { text: `Not added — ${r.error || 'unexpected answer'}`, tone: 'err' });
        return;
      }
      const mid = r.data.mtr_lead_id ? String(r.data.mtr_lead_id) : 'yes';
      const cur = this.detailFor(id);
      if (cur) cur.lead.mtr_lead_id = mid;
      const row = (this.data?.leads || []).find(l => l.id === id);
      if (row) row.mtr_lead_id = mid;
      if (this.opts.onPipelineAdded) { try { this.opts.onPipelineAdded(); } catch (e) { console.error('[leads] onPipelineAdded', e); } }
      this.pNote(id, { text: r.data.created === false ? 'Already on the pipeline board.' : 'Added to the MTR pipeline.', tone: 'ok' });
    } finally {
      this.pipelineBusy = false;
      this.patchProfile();
    }
  }

  // ── styles ────────────────────────────────────────────────────────────────
  private css(): string {
    return `<style>
      .lx { --lx-h: calc(100vh - 150px); color:var(--text); }
      .lx-kpis { display:grid; grid-template-columns:repeat(5, minmax(0,1fr)); gap:10px; margin-bottom:10px; }
      .lx-kpi { background:var(--card); border:1px solid var(--border); border-radius:12px; padding:11px 14px 10px; min-width:0; }
      .lx-kpi-l { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .lx-kpi-v { font-size:22px; font-weight:700; letter-spacing:-.02em; margin-top:3px; font-variant-numeric:tabular-nums; line-height:1.2; }
      .lx-kpi-c { font-size:11px; color:var(--text-faint); margin-top:1px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .lx-kpi.warn { background:var(--warning-soft); border-color:#fde68a; }
      .lx-kpi.warn .lx-kpi-v { color:var(--warning-text); }
      .lx-kpi.bad { background:var(--danger-soft); border-color:var(--danger-border, #fecaca); }
      .lx-kpi.bad .lx-kpi-v { color:var(--danger-text); }

      .lx-status { display:flex; flex-wrap:wrap; align-items:center; gap:4px 7px; font-size:12px; color:var(--text-muted); padding:0 2px 10px; }
      .lx-status b { color:var(--text); font-weight:600; }
      .lx-status-r { margin-left:auto; display:inline-flex; align-items:center; gap:12px; }
      .lx-sep { color:var(--text-faint); }
      .lx-dot { display:inline-block; width:7px; height:7px; border-radius:50%; background:var(--text-faint); margin-right:6px; vertical-align:1px; }
      .lx-dot.on { background:#10b981; box-shadow:0 0 0 3px rgba(16,185,129,.16); }
      .lx-ps.warn, .lx-ps.warn b { color:var(--warning-text); }
      .lx-ps.bad, .lx-ps.bad b { color:var(--danger-text); }
      .lx-ps.off { color:var(--text-faint); }
      .lx-link { border:none; background:none; padding:0; font:inherit; font-size:12px; font-weight:500; color:var(--info); cursor:pointer; text-decoration:none; }
      .lx-link:hover { text-decoration:underline; }
      .lx-callout { display:flex; align-items:center; gap:12px; flex-wrap:wrap; padding:10px 14px; border-radius:12px; font-size:12.5px; line-height:1.45; margin-bottom:10px; border:1px solid #fde68a; background:var(--warning-soft); color:var(--warning-text); }
      .lx-callout > span { flex:1; min-width:220px; }
      .lx-callout.grey { border-color:var(--border); background:var(--bg-subtle); color:var(--text-muted); }

      .lx-btn { display:inline-flex; align-items:center; justify-content:center; gap:5px; padding:7px 14px; border:1px solid var(--border); border-radius:var(--radius-btn, 10px); background:var(--card); color:var(--text); font:inherit; font-size:12.5px; font-weight:600; line-height:1.3; cursor:pointer; white-space:nowrap; text-decoration:none; transition:background .12s ease, border-color .12s ease; }
      .lx-btn:hover { background:var(--bg-subtle); border-color:var(--border-strong); }
      .lx-btn.primary { background:var(--text); border-color:var(--text); color:#fff; }
      .lx-btn.primary:hover { background:#1e293b; }
      .lx-btn.sm { padding:4px 10px; font-size:11.5px; border-radius:8px; }
      .lx-btn:disabled { opacity:.45; cursor:default; }
      .lx-icon { border:none; background:none; color:var(--text-faint); cursor:pointer; font-size:12px; line-height:1; padding:4px 6px; border-radius:6px; flex:none; }
      .lx-icon:hover { color:var(--text); background:var(--bg-subtle); }
      .lx-in { border:1px solid var(--border); border-radius:8px; padding:6px 10px; font:inherit; font-size:12px; color:var(--text); background:var(--card); min-width:0; box-sizing:border-box; }
      .lx-in:focus, .lx-ta:focus { outline:none; border-color:var(--accent-border); box-shadow:var(--focus-ring); }
      .lx-wide { width:100%; }
      .lx-meta { font-size:11.5px; color:var(--text-muted); line-height:1.45; }
      .lx-faint { color:var(--text-faint); }

      .lx-wrap { display:grid; grid-template-columns:340px minmax(0,1fr); grid-template-rows:minmax(0,1fr); height:var(--lx-h); min-height:520px; background:var(--card); border:1px solid var(--border); border-radius:12px; overflow:hidden; position:relative; box-shadow:var(--shadow-card); }
      .lx-wrap.has-sel { grid-template-columns:340px minmax(0,1fr) 300px; }
      .lx-list { display:flex; flex-direction:column; min-height:0; min-width:0; border-right:1px solid var(--border); }
      .lx-filters { padding:10px 12px; border-bottom:1px solid var(--border); display:flex; flex-direction:column; gap:8px; }
      .lx-frow { display:flex; align-items:center; gap:8px; }
      .lx-count { margin-left:auto; font-size:11px; color:var(--text-faint); font-variant-numeric:tabular-nums; }
      .lx-seg { display:inline-flex; background:var(--bg-subtle); border:1px solid var(--border); border-radius:9px; padding:2px; gap:2px; }
      .lx-seg-b { border:none; background:none; font:inherit; font-size:11.5px; font-weight:600; color:var(--text-muted); padding:3px 11px; border-radius:7px; cursor:pointer; }
      .lx-seg-b:hover { color:var(--text); }
      .lx-seg-b.on { background:var(--card); color:var(--text); box-shadow:0 1px 2px rgba(15,23,42,.08); }
      .lx-chips { display:flex; flex-wrap:wrap; gap:5px; }
      .lx-chip { display:inline-flex; align-items:center; gap:5px; font:inherit; font-size:11px; font-weight:600; padding:3px 9px; border:1px solid var(--border); border-radius:999px; background:var(--card); color:var(--text-muted); cursor:pointer; }
      .lx-chip:hover { border-color:var(--border-strong); color:var(--text); }
      .lx-chip.on { background:var(--text); border-color:var(--text); color:#fff; }
      .lx-chip-n { font-weight:500; opacity:.7; font-variant-numeric:tabular-nums; }
      .lx-rows { flex:1; overflow-y:auto; min-height:0; }
      .lx-row { display:block; width:100%; text-align:left; font:inherit; color:var(--text); border:none; border-bottom:1px solid var(--border-soft); border-left:3px solid transparent; background:none; padding:10px 12px 10px 11px; cursor:pointer; transition:background .1s ease; }
      .lx-row:hover { background:var(--bg-subtle); }
      .lx-row.on { background:var(--accent-soft); }
      .lx-row.waiting { border-left-color:#f59e0b; }
      .lx-row.breached { border-left-color:var(--danger); }
      .lx-row.partial .lx-row-name, .lx-row.partial .lx-row-prev { opacity:.65; }
      .lx-row-top { display:flex; align-items:baseline; gap:8px; }
      .lx-row-name { flex:1; min-width:0; font-size:13px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .lx-row.waiting .lx-row-name { font-weight:700; }
      .lx-wdot { display:inline-block; width:7px; height:7px; border-radius:50%; background:#f59e0b; margin-right:6px; vertical-align:1px; }
      .lx-row.breached .lx-wdot { background:var(--danger); }
      .lx-row-time { flex:none; font-size:11px; color:var(--text-faint); font-variant-numeric:tabular-nums; }
      .lx-row-sub { display:flex; align-items:center; gap:6px; margin-top:3px; min-width:0; }
      .lx-row-prop { font-size:11.5px; color:var(--text-muted); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; min-width:0; }
      .lx-row-prev { font-size:12px; color:var(--text-muted); margin-top:4px; line-height:1.4; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; overflow-wrap:anywhere; }
      .lx-row-foot { display:flex; align-items:center; flex-wrap:wrap; gap:5px; margin-top:7px; }
      .lx-plat { display:inline-block; flex:none; font-size:10px; font-weight:700; letter-spacing:.02em; padding:1px 7px; border-radius:999px; background:var(--bg-subtle); color:var(--text-muted); }
      .lx-plat.p-zillow { background:var(--info-soft); color:var(--info-text); }
      .lx-plat.p-ff { background:#f5f3ff; color:#5b21b6; }
      .lx-plat.p-avail { background:#ecfeff; color:#0e7490; }
      .lx-sla, .lx-badge { display:inline-flex; align-items:center; gap:3px; font-size:10.5px; font-weight:600; padding:1px 7px; border-radius:999px; white-space:nowrap; background:var(--bg-subtle); color:var(--text-muted); }
      .lx-sla.warn { background:var(--warning-soft); color:var(--warning-text); }
      .lx-sla.bad { background:var(--danger-soft); color:var(--danger-text); }
      .lx-badge.warn { background:var(--danger-soft); color:var(--danger-text); }
      .lx-badge.q { background:var(--info-soft); color:var(--info-text); }
      .lx-badge.dim { background:none; border:1px dashed var(--border-strong); color:var(--text-faint); font-weight:500; }
      .lx-badge.draft { background:none; color:var(--text-muted); font-weight:500; padding-left:2px; }
      .lx-av { margin-left:auto; width:22px; height:22px; flex:none; border-radius:50%; background:var(--bg-subtle); border:1px solid var(--border); color:var(--text-muted); font-size:9.5px; font-weight:700; display:inline-flex; align-items:center; justify-content:center; }

      .lx-main { display:flex; flex-direction:column; min-width:0; min-height:0; }
      .lx-pick { margin:auto; text-align:center; color:var(--text-muted); font-size:13px; padding:30px; }
      .lx-pick-ic { font-size:28px; margin-bottom:8px; opacity:.8; }
      .lx-head { display:flex; align-items:center; gap:10px; padding:10px 16px; border-bottom:1px solid var(--border); min-height:58px; }
      .lx-head-t { flex:1; min-width:0; }
      .lx-head-name { display:flex; align-items:center; gap:7px; flex-wrap:wrap; }
      .lx-head-nm { font-size:14.5px; font-weight:700; letter-spacing:-.01em; }
      .lx-head-sub { font-size:12px; color:var(--text-muted); margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .lx-back, .lx-prof-toggle { display:none !important; }
      .lx-msgs { flex:1; min-height:0; overflow-y:auto; padding:18px 20px; display:flex; flex-direction:column; gap:10px; background:var(--bg-subtle); }
      .lx-b { max-width:76%; padding:9px 13px 7px; border-radius:14px; font-size:13px; line-height:1.5; }
      .lx-b.in { align-self:flex-start; background:var(--card); border:1px solid var(--border); border-bottom-left-radius:4px; }
      .lx-b.out { align-self:flex-end; background:var(--accent-soft); border:1px solid var(--accent-border); border-bottom-right-radius:4px; }
      .lx-b.pending { background:var(--card); border:1px dashed var(--accent-border); }
      .lx-b.pending .lx-b-text { color:var(--text-muted); }
      .lx-b.problem { background:var(--warning-soft); border:1px solid #fde68a; }
      .lx-b-text { white-space:pre-wrap; overflow-wrap:anywhere; color:var(--text); }
      .lx-b-meta { display:flex; flex-wrap:wrap; align-items:center; gap:6px; font-size:10.5px; color:var(--text-faint); margin-top:4px; }
      .lx-b.out .lx-b-meta { justify-content:flex-end; }
      .lx-b-meta .lx-link { font-size:10.5px; }
      .lx-b-warn { font-size:11.5px; font-weight:600; color:var(--warning-text); margin-top:5px; overflow-wrap:anywhere; }
      .lx-sys { align-self:center; max-width:86%; text-align:center; font-size:11.5px; color:var(--text-muted); background:var(--card); border:1px solid var(--border); border-radius:12px; padding:4px 12px; white-space:pre-wrap; overflow-wrap:anywhere; }
      .lx-note { align-self:center; width:min(86%, 560px); background:#fefce8; border:1px solid #fde68a; border-radius:10px; padding:8px 12px; font-size:12.5px; line-height:1.5; }
      .lx-note-h { display:flex; justify-content:space-between; gap:8px; font-size:10.5px; font-weight:700; color:#a16207; text-transform:uppercase; letter-spacing:.04em; margin-bottom:3px; }
      .lx-note-by { font-weight:500; text-transform:none; letter-spacing:0; color:#a16207; opacity:.85; }
      .lx-hint { margin:0; padding:6px 14px; font-size:11.5px; color:var(--warning-text); background:var(--warning-soft); border-top:1px solid #fde68a; }
      .lx-compose { border-top:1px solid var(--border); padding:10px 14px 12px; display:flex; flex-direction:column; gap:8px; background:var(--card); }
      .lx-ai { display:flex; gap:8px; align-items:center; }
      .lx-ai .lx-in { flex:1; }
      .lx-ta { width:100%; min-height:74px; max-height:40vh; resize:vertical; border:1px solid var(--border); border-radius:10px; padding:9px 12px; font:inherit; font-size:13px; line-height:1.5; color:var(--text); background:var(--card); box-sizing:border-box; }
      .lx-ta[readonly] { background:var(--bg-subtle); color:var(--text-muted); }
      .lx-crow { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
      .lx-status-sel { padding:4px 8px; font-size:11.5px; }
      .lx-cnote { flex:1; min-width:140px; font-size:12px; color:var(--text-muted); line-height:1.35; }
      .lx-cnote.err { color:var(--danger-text); }
      .lx-cnote.ok { color:var(--success-text); }

      .lx-profile { border-left:1px solid var(--border); overflow-y:auto; min-height:0; background:var(--card); }
      .lx-prof-head { display:none; align-items:center; justify-content:space-between; padding:10px 16px; border-bottom:1px solid var(--border); font-size:13px; font-weight:700; position:sticky; top:0; background:var(--card); z-index:1; }
      .lx-pnote { margin:10px 16px 0; font-size:12px; padding:6px 10px; border-radius:8px; background:var(--bg-subtle); color:var(--text-muted); }
      .lx-pnote.err { background:var(--danger-soft); color:var(--danger-text); }
      .lx-pnote.ok { background:var(--success-soft); color:var(--success-text); }
      .lx-sec { padding:12px 16px; border-bottom:1px solid var(--border-soft); }
      .lx-sec:last-child { border-bottom:none; }
      .lx-sec-h { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-faint); margin-bottom:7px; }
      .lx-f { display:flex; align-items:center; gap:6px; min-height:28px; }
      .lx-f.edit .lx-in { flex:1; }
      .lx-f-l { width:46px; flex:none; font-size:11.5px; color:var(--text-muted); }
      .lx-f-v { flex:1; min-width:0; font-size:12.5px; overflow-wrap:anywhere; }
      .lx-facts { width:100%; border-collapse:collapse; font-size:12px; }
      .lx-facts th { text-align:left; font-weight:500; color:var(--text-muted); padding:3px 10px 3px 0; vertical-align:top; white-space:nowrap; width:1%; }
      .lx-facts td { padding:3px 0; overflow-wrap:anywhere; white-space:pre-wrap; }
      .lx-tags { display:flex; flex-wrap:wrap; gap:4px; }
      .lx-tag { font-size:10.5px; padding:1px 8px; border-radius:999px; background:var(--bg-subtle); border:1px solid var(--border); color:var(--text-muted); }
      .lx-tag.strong { background:var(--accent-soft); border-color:var(--accent-border); color:var(--accent-hover); font-weight:600; }
      .lx-dup { margin-top:8px; font-size:11.5px; color:var(--warning-text); background:var(--warning-soft); border-radius:8px; padding:6px 9px; line-height:1.45; }
      .lx-ok { color:var(--success-text); font-weight:600; font-size:12.5px; display:flex; gap:10px; align-items:center; }
      .lx-hist summary { cursor:pointer; font-size:12px; font-weight:600; color:var(--text-muted); }
      .lx-hist ul { list-style:none; margin:8px 0 0; padding:0; display:flex; flex-direction:column; gap:7px; }
      .lx-hist li { display:flex; flex-direction:column; font-size:11.5px; line-height:1.4; overflow-wrap:anywhere; }

      .lx-empty { padding:28px 18px; text-align:center; color:var(--text-muted); font-size:12.5px; line-height:1.5; }
      .lx-big { background:var(--card); border:1px solid var(--border); border-radius:12px; padding:44px 24px; text-align:center; }
      .lx-big-ic { font-size:34px; margin-bottom:8px; }
      .lx-big h3 { margin:0 0 6px; font-size:16px; font-weight:700; color:var(--text); }
      .lx-big p { margin:0 auto 16px; max-width:560px; font-size:13px; line-height:1.6; color:var(--text-muted); }

      @media (max-width:1360px) {
        .lx-wrap.has-sel { grid-template-columns:320px minmax(0,1fr); }
        .lx-profile { position:absolute; top:0; right:0; bottom:0; width:320px; max-width:100%; z-index:4; box-shadow:var(--shadow-pop); display:none; }
        .lx-wrap.prof-open .lx-profile { display:block; }
        .lx-prof-toggle { display:inline-flex !important; }
        .lx-prof-head { display:flex; }
      }
      @media (max-width:1100px) { .lx-kpis { grid-template-columns:repeat(3, minmax(0,1fr)); } }
      @media (max-width:900px) {
        .lx { --lx-h: calc(100vh - 96px); }
        .lx-kpis { grid-template-columns:repeat(2, minmax(0,1fr)); }
        .lx-wrap, .lx-wrap.has-sel { grid-template-columns:minmax(0,1fr); min-height:460px; }
        .lx-wrap.has-sel .lx-list { display:none; }
        .lx-wrap:not(.has-sel) .lx-main { display:none; }
        .lx-list { border-right:none; }
        .lx-back { display:inline-flex !important; }
        .lx-profile { width:100%; }
        .lx-head { padding:8px 10px; gap:6px; flex-wrap:wrap; }
        .lx-head .lx-back { margin-right:auto; }
        .lx-head-t { order:1; flex:1 0 100%; }
        .lx-head-sub { white-space:normal; }
        .lx-crow .lx-btn.primary { margin-left:auto; }
        .lx-msgs { padding:12px; }
        .lx-b { max-width:90%; }
        .lx-status-r { margin-left:0; }
      }
    </style>`;
  }
}
